import {
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import { StoreService } from 'src/app/store.service';
import { ActivatedRoute, Router } from '@angular/router';
import { UtilitiesService } from 'src/app/core/utilities.service';
import { environment } from '../../../environments/environment';
import { combineLatest, Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbDropdown, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EditorState, EditorSelection } from '@codemirror/state';
// EditorView and basicSetup must come from the individual @codemirror/* packages —
// importing from the 'codemirror' meta-package causes duplicate @codemirror/state
// instances in AOT/production webpack builds, breaking CodeMirror at runtime.
import {
  EditorView,
  lineNumbers, highlightActiveLineGutter, highlightSpecialChars,
  drawSelection, dropCursor, rectangularSelection, crosshairCursor,
  highlightActiveLine, keymap,
} from '@codemirror/view';
import {
  foldGutter, indentOnInput, syntaxHighlighting,
  defaultHighlightStyle, bracketMatching, foldKeymap,
  syntaxTree,
} from '@codemirror/language';
import { history, defaultKeymap, historyKeymap } from '@codemirror/commands';
import {
  search,
  searchKeymap,
  openSearchPanel,
  closeSearchPanel,
  highlightSelectionMatches,
} from '@codemirror/search';
import {
  closeBrackets, autocompletion,
  closeBracketsKeymap, completionKeymap,
} from '@codemirror/autocomplete';
import { lintKeymap } from '@codemirror/lint';

const basicSetup = [
  lineNumbers(),
  highlightActiveLineGutter(),
  highlightSpecialChars(),
  history(),
  foldGutter(),
  drawSelection(),
  dropCursor(),
  EditorState.allowMultipleSelections.of(true),
  indentOnInput(),
  syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
  bracketMatching(),
  closeBrackets(),
  autocompletion(),
  rectangularSelection(),
  crosshairCursor(),
  highlightActiveLine(),
  highlightSelectionMatches(),
  search({ top: true }),
  keymap.of([
    ...closeBracketsKeymap,
    ...defaultKeymap,
    ...historyKeymap,
    ...foldKeymap,
    ...completionKeymap,
    ...lintKeymap,
    ...searchKeymap,
  ]),
];

import { DataService } from '../../data.service';
import { DealerService } from 'src/app/dealer.service';
import { html } from '@codemirror/lang-html';
import { UploadImageComponent } from '../upload-image/upload-image.component';
import {
  HtmlPageTemplate,
  HtmlPageTemplateStyle,
} from './html-template.model';
import {
  javascriptLanguage,
  scopeCompletionSource,
} from '@codemirror/lang-javascript';
import {
  ClassicConversionReport,
  convertClassicPage,
} from './classic-to-studio/classic-to-studio.converter';

enum SAVE_BEHAVIOUR {
  SAVE = 0,
  SAVE_AND_EDIT = 1,
  SAVE_AND_CREATE_NEW = 2,
}

enum PAGE_MODE {
  CREATE = 'create',
  EDIT = 'update',
}

@Component({
  selector: 'app-page-edit',
  templateUrl: './page-manage.component.html',
  styleUrls: ['./page-manage.component.scss'],
})
export class PageManageComponent implements OnInit {
  @ViewChild('dialogTemplate') dialogTemplate: TemplateRef<any>;
  @ViewChild('pageJsRef', { static: false }) pageJsEl;
  @ViewChild('returnConfirmModal') returnConfirmModal;
  @ViewChild('dropdown') dropdown: NgbDropdown;
  @ViewChild('newEditorIframe', { static: false })
  newEditorIframe!: ElementRef<HTMLIFrameElement>;

  private readonly IFRAME_ORIGIN = environment.editorBaseUrl;
  private iframeMessageHandler!: (event: MessageEvent) => void;
  private isIframeAppReady = false;
  private pendingIframeInitMessage: any = null;
  // The editor window an init was last posted to, until its 'ready' answers it
  private iframeInitTarget: Window | null = null;
  private themeObserver: MutationObserver | null = null;
  private cachedSafeUrl: any = null;
  private currentIframeUrl: string = '';
  pageThemeList = [];
  productSeries = [];
  productNameBySeries = {};
  selectedProductKey = '';

  isLoadingSelectedTheme;
  pageMode;

  showPreviewButton = false;
  duplicateChecking;
  environment = environment;

  // Basic states
  storeId;
  bearer;
  storePageId;
  currentEditPageUrl;
  country_code;
  language_code;
  language_name;
  languages = [];
  featureList;
  enableAIPageTemplate = false;
  checkByCredit = false;

  // Media Editor variables for text editor
  ogimgURL;
  ogImgPhotoId;
  // Data states
  customPageIsLoading = false;
  customPageIsSaving = false;
  fullPageMode = false;
  editorIsReady = false;
  readyToSave = false;
  isValidUrl = false;
  validatingURL = false;
  editPageIsloading = false;
  changeCountryIsLoading = false;
  hasUnsavedChanges = false;
  plan = '';
  affiliateBuddyList = [];
  selectedABOptions = [];

  // Connect enum
  SAVE_BEHAVIOUR = SAVE_BEHAVIOUR;
  PAGE_MODE = PAGE_MODE;

  // Page url slug regex (lowercase letters, numbers, and hyphens only)
  urlSlugRegex: RegExp = /[^a-z0-9-]/g;
  pageUrlSlugWarning: string = '';

  // Pagesteps schema
  stepSchema = [
    {
      name: 'Create Name',
      title: 'Begin Your Success Story Now!',
      subtext: `Choose a name and URL for your landing page. Make it memorable to boost conversions and drive success!`,
      beforeContinue: async () => {
        return await this.validatePageUrl();
      },
    },
    {
      name: 'Select Products',
      title: 'Select Product ',
      subtext: `Choose a product series you'd like to feature and showcase in your storefront.`,
      beforeContinue: () => {
        // Validate product selection before leaving this step
        if (this.generateMethod === 0 && !this.selectedProduct) {
          this.utils.notify.showWarning(
            'Please select a product before continuing!',
          );
          return false;
        }
        // If generateMethod === 1 (custom), no product selection needed
        return true;
      },
      guard: () => {
        const errors = [];
        // A home page has no URL field — it answers on the storefront's home
        // address, and its slug is assigned by the backend on save.
        if (!this.customPageData.page_url && !this.isHomePage) {
          errors.push('Page URL is required!');
        }
        if (!this.customPageData.page_title) {
          errors.push('Page title is required!');
        }
        if (this.validatingURL) {
          errors.push('Validating URL');
        }
        return errors;
      },
      onEnter: () => {
        this.generateProductNamesInSeries();
        this.getAffiliateList();
      },
    },
    {
      name: 'Select Editor',
      title: 'Choose Your Editor',
      subtext: `Select the editor you'd like to use for designing your page.`,
      guard: () => {
        // No guard when entering - user needs to be on this step to select editor
        return [];
      },
      beforeContinue: () => {
        // Validate editor selection before leaving this step
        if (!this.selectedEditorType) {
          this.utils.notify.showWarning(
            'Please select an editor type before continuing!',
          );
          return false;
        }
        return true;
      },
      onEnter: () => {
        // Studio is the default since the Classic editor was retired
        if (!this.isEditPage && !this.selectedEditorType) {
          this.onSelectEditor('new');
        }
      },
    },
    {
      name: 'Design page',
      title: 'Select a Template',
      subtext: `Choose the perfect template to showcase your content and make a lasting impression online.`,
      guard: () => {
        const errors = [];
        // A home page is never generated from a product — Select Products is hidden
        // for it — so there is nothing here to check.
        if (this.isHomePage) {
          return errors;
        }
        if (
          !(
            (this.generateMethod === 0 && this.selectedProduct) ||
            this.generateMethod === 1
          )
        ) {
          errors.push('Please select product series and product unit!');
        }
        return errors;
      },
      sidenav: [
        {
          id: 'template',
          label: 'Template',
          iconSVG: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.5 2L14.5 22M12 12L2 14.5M2 4.5C2 3.83696 2.26339 3.20107 2.73223 2.73223C3.20107 2.26339 3.83696 2 4.5 2H19.5C20.163 2 20.7989 2.26339 21.2678 2.73223C21.7366 3.20107 22 3.83696 22 4.5V19.5C22 20.163 21.7366 20.7989 21.2678 21.2678C20.7989 21.7366 20.163 22 19.5 22H4.5C3.83696 22 3.20107 21.7366 2.73223 21.2678C2.26339 20.7989 2 20.163 2 19.5V4.5Z" stroke="#0B5AFE" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>`,
          onEnter: () => {
            if (this.selectedTheme === '' || this.selectedTheme === undefined) {
              // TODO: Hardcode template
              if (+this.generateMethod === 1) {
                // Blank Template
                this.selectedTheme = -1;
              } else {
                // get default template
                // if (this.isTaiwan) {
                //   // TW Template
                //   this.selectedTheme = 19;
                // } else {
                //   // AI Template
                //   this.selectedTheme = 17;
                // }
                // this.getDefaultTemplate()
              }
            }
          },
        },
        {
          id: 'content',
          label: 'Content',
          hideTitle: true,
          iconSVG: `<svg width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19.2988 9.9574L18.1778 8.8364C17.9902 8.6489 17.8847 8.39453 17.8846 8.12926C17.8846 7.86399 17.9898 7.60954 18.1773 7.4219C18.3649 7.23426 18.6192 7.1288 18.8845 7.1287C19.1498 7.12861 19.4042 7.2339 19.5918 7.4214L22.4198 10.2504C22.6073 10.4379 22.7126 10.6922 22.7126 10.9574C22.7126 11.2226 22.6073 11.4769 22.4198 11.6644L19.5918 14.4924C19.4032 14.6746 19.1506 14.7754 18.8884 14.7731C18.6263 14.7708 18.3754 14.6656 18.19 14.4802C18.0046 14.2948 17.8995 14.044 17.8972 13.7818C17.8949 13.5196 17.9957 13.267 18.1778 13.0784L19.2988 11.9574H12.8138V18.4424L13.9348 17.3214C14.1235 17.1392 14.3761 17.0385 14.6383 17.0407C14.9004 17.043 15.1513 17.1482 15.3367 17.3336C15.5221 17.519 15.6272 17.7698 15.6295 18.032C15.6318 18.2942 15.531 18.5468 15.3488 18.7354L12.5208 21.5644C12.3333 21.7519 12.079 21.8572 11.8138 21.8572C11.5487 21.8572 11.2944 21.7519 11.1068 21.5644L8.27785 18.7354C8.09569 18.5468 7.9949 18.2942 7.99717 18.032C7.99945 17.7698 8.10462 17.519 8.29003 17.3336C8.47544 17.1482 8.72625 17.043 8.98845 17.0407C9.25064 17.0385 9.50325 17.1392 9.69185 17.3214L10.8138 18.4424V11.9574H4.32785L5.44985 13.0784C5.63201 13.267 5.7328 13.5196 5.73052 13.7818C5.72824 14.044 5.62308 14.2948 5.43767 14.4802C5.25226 14.6656 5.00145 14.7708 4.73925 14.7731C4.47705 14.7754 4.22445 14.6746 4.03585 14.4924L1.20685 11.6644C1.01938 11.4769 0.914062 11.2226 0.914062 10.9574C0.914062 10.6922 1.01938 10.4379 1.20685 10.2504L4.03585 7.4204C4.22362 7.2329 4.47819 7.12766 4.74356 7.12785C5.00892 7.12804 5.26334 7.23363 5.45085 7.4214C5.63836 7.60918 5.74359 7.86375 5.7434 8.12911C5.74322 8.39447 5.63762 8.6489 5.44985 8.8364L4.32785 9.9564H10.8138V3.4734L9.69185 4.5934C9.50325 4.77556 9.25064 4.87636 8.98845 4.87408C8.72625 4.8718 8.47544 4.76663 8.29003 4.58122C8.10462 4.39581 7.99945 4.145 7.99717 3.8828C7.9949 3.62061 8.09569 3.36801 8.27785 3.1794L11.1068 0.350404C11.2944 0.162933 11.5487 0.0576172 11.8138 0.0576172C12.079 0.0576172 12.3333 0.162933 12.5208 0.350404L15.3488 3.1794C15.4444 3.27165 15.5205 3.382 15.573 3.504C15.6254 3.626 15.6529 3.75722 15.6541 3.89C15.6553 4.02278 15.63 4.15446 15.5797 4.27736C15.5294 4.40025 15.4551 4.51191 15.3612 4.6058C15.2674 4.69969 15.1557 4.77394 15.0328 4.82423C14.9099 4.87451 14.7782 4.89981 14.6454 4.89865C14.5127 4.8975 14.3814 4.86991 14.2594 4.8175C14.1374 4.7651 14.0271 4.68891 13.9348 4.5934L12.8138 3.4724V9.9574H19.2988Z" fill="#0B5AFE"/>
          </svg>`,
          guard: () => {
            return true;
          },
        },
        /*{
          id: 'html-editor',
          label: 'HTML Editor',
          hideTitle: true,
          isComingSoon: true,
          disabled: true,
          iconSVG: `<svg width="20" height="22" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg">
          <mask id="mask0_1446_38990" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="22">
          <path d="M16.902 1.5H3.09803C2.95883 1.50002 2.82117 1.52909 2.69386 1.58537C2.56655 1.64164 2.45238 1.72388 2.35868 1.82681C2.26497 1.92974 2.19379 2.05109 2.14968 2.18312C2.10557 2.31514 2.08951 2.45491 2.10253 2.5935L3.44653 16.9265C3.4628 17.1009 3.52463 17.268 3.6258 17.4111C3.72698 17.5541 3.86396 17.6681 4.02303 17.7415L9.58103 20.3065C9.71239 20.3671 9.85535 20.3985 10 20.3985C10.1447 20.3985 10.2877 20.3671 10.419 20.3065L15.977 17.7415C16.1362 17.6681 16.2733 17.5542 16.3745 17.4112C16.4758 17.2681 16.5377 17.101 16.554 16.9265L17.8975 2.5935C17.9105 2.45491 17.8945 2.31514 17.8504 2.18312C17.8063 2.05109 17.7351 1.92974 17.6414 1.82681C17.5477 1.72388 17.4335 1.64164 17.3062 1.58537C17.1789 1.52909 17.0412 1.50002 16.902 1.5Z" fill="white" stroke="white" stroke-width="2.4"/>
          <path d="M14 5H6L6.5 9.5H13.5L13 15L10 16.5L7 15L6.75 12.5" stroke="black" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
          </mask>
          <g mask="url(#mask0_1446_38990)">
          <path d="M-2 -1H22V23H-2V-1Z" fill="#0B5AFE"/>
          </g>
          </svg>`,
          guard: () => {
            return false;
          },
        },*/
      ],
    },
    {
      name: 'Settings',
      title: 'Page Settings',
      guard: () => {
        const errors = [];
        // Studio pages carry no classic store_page_theme — their template is
        // selectedStudioTemplate — so selectedTheme is meaningless here, and is
        // falsy for any Studio page saved through the product flow (page_theme
        // is never assigned on that path). Because the Design page's Continue
        // button reads this guard (canContinue → testGuardForStep(currentStep+1)),
        // gating on selectedTheme left it permanently disabled in edit mode no
        // matter which card was picked. The template subpage has its own gate in
        // nextStep(), which warns rather than silently disabling.
        if (this.selectedEditorType === 'new') {
          return errors;
        }
        if (!this.selectedTheme) {
          errors.push('Please select page template before continue');
        }
        if (this.isLoadingSelectedTheme) {
          errors.push('Loading for page theme');
        }
        return errors;
      },
      onEnter: () => {
        let pageJsElement = this.pageJsEl?.nativeElement;

        try {
          if (pageJsElement) {
            const windowCompletions = javascriptLanguage.data.of({
              autocomplete: scopeCompletionSource((window as any).cleanWindow),
            });

            this.pageJsState = EditorState.create({
              doc: this.customPageData.page_content.js ?? '',
              extensions: [
                basicSetup,
                html(),
                windowCompletions,
                EditorView.updateListener.of((e) => {
                  if (e.docChanged) {
                    const newDoc = e.state.doc.toString();
                    this.pageJsValue = newDoc;
                    this.customPageData.page_content.js = newDoc;
                  }
                }),
              ],
            });

            this.pageJsView = new EditorView({
              state: this.pageJsState,
              parent: pageJsElement,
            });
          }
        } catch (e) {
          console.error('ERROR EditorState', e);
        }
      },
      subtext: `Customize layout, metadata, and content effortlessly for a personalized and engaging user experience.`,
    },
  ];

  pageJsState;
  pageJsValue;
  pageJsView;

  htmlEditorView: EditorView;
  htmlEditorContent = '';
  // HTML editor "Select a Template" step — loaded at runtime from the backend; see
  // loadHtmlTemplatesFromApi(). One list for every wizard flow: any row tagged
  // theme_categories_id=5 is an HTML-editor template (store_page_theme.type no
  // longer matters).
  htmlTemplates: HtmlPageTemplate[] = [];
  isLoadingHtmlTemplates = false;
  /** Set once the htmlTemplates list has been fetched; the list is flow-independent. */
  private htmlTemplatesLoaded = false;
  /** store_page_theme.theme_categories_id that marks a row as an HTML-editor template. */
  private readonly HTML_TEMPLATE_CATEGORY_ID = 5;
  /**
   * Maps a backend theme_name to the template's stable string id. The id drives the
   * picker thumbnail accent via the .html-template-thumb[data-template='…'] rules in
   * page-manage.component.scss. (The list response carries no string id; a template
   * without an entry falls back to its numeric id and a neutral thumbnail.)
   */
  private readonly STORE_THEME_IDS: { [name: string]: string } = {
    Blank: 'blank',
    'FAQ Page': 'faq',
    'How-To Guide': 'how-to',
    'Blog Article': 'blog',
    'Product Page': 'product',
    'Service Page': 'service',
    'Comparison Page': 'comparison',
    'Review Page': 'review',
    'Pricing Page': 'pricing',
    'Local Business Page': 'local-business',
    'Landing Page': 'landing',
    'Category Page': 'category',
    'Knowledge Base': 'knowledge-base',
    'Glossary Page': 'glossary',
    'Author Profile': 'author-profile',
    'Location Landing Page': 'location-landing',
  };
  selectedHtmlTemplate: string = null;
  htmlTemplateSelected = false;
  /**
   * True while the product HTML is being generated server-side (Flowise, ~15s).
   * Replaces the template picker with the progress panel and makes Continue a
   * no-op, so the selection can't change mid-request and land the generated
   * content under the wrong template.
   */
  isGeneratingProductHtml = false;
  /**
   * The open template's group variants ("Styles"), or empty when the open template
   * is standalone. Drives the editor toolbar's Styles dropdown, which is hidden
   * unless there's more than one.
   */
  activeTemplateStyles: HtmlPageTemplateStyle[] = [];
  /** store_page_theme_id of the Style currently loaded in the editor. */
  activeStyleId: number | string = null;
  // Edit mode: snapshot of the page's content taken when the template picker is
  // reopened. If the user returns without choosing a new template ("Current page"),
  // this is restored so the existing page isn't overwritten by a template default.
  private editHtmlSnapshot: string = null;
  // Edit mode: the template id the page was built from, read from an HTML-comment
  // marker embedded in its content. Pre-selects that template in the picker and lets a
  // "keep current" Continue (template unchanged) be told apart from a real switch. null
  // for pages with no marker (legacy / saved before the marker existed).
  private editHtmlTemplateId: string = null;
  // Name of the HTML comment stamped into saved content to remember its source
  // template, e.g. `<!-- estore-template: product -->`. Invisible when rendered; rides
  // along in page_content so edit mode can re-select the template with no backend change.
  private readonly HTML_TEMPLATE_MARKER = 'estore-template';
  htmlPreviewFullscreen = false;
  /**
   * Markup last written to each preview iframe, so updateHtmlPreview() can skip a
   * redundant srcdoc assignment (each one reloads the frame and flashes). Reset to
   * null whenever a frame is unmounted, since the next mount starts blank.
   */
  private lastRenderedPreviewHtml: string | null = null;
  private lastRenderedFullscreenHtml: string | null = null;
  // Preview frame element the click-to-code listener is currently bound to. The
  // frame lives behind *ngIf, so a recreated element no longer matches and gets
  // the listener re-attached (see attachPreviewClickToCode).
  private previewClickBoundFrame: HTMLIFrameElement | null = null;
  // Editor position selected by a preview click made while in preview-only mode —
  // the code pane was hidden (unmeasurable) then, so the scroll is deferred until
  // the user switches to a mode that shows it (see setHtmlEditorViewMode).
  private pendingEditorScrollPos: { from: number; to: number } | null = null;
  htmlEditorLineCount = 0;
  htmlEditorCharCount = 0;
  htmlEditorCursorLine = 1;
  htmlEditorCursorCol = 1;
  htmlEditorViewMode: 'split' | 'code' | 'preview' = 'split';
  previewDeviceMode: 'desktop' | 'tablet' | 'mobile' = 'desktop';

  setPreviewDeviceMode(mode: 'desktop' | 'tablet' | 'mobile') {
    this.previewDeviceMode = mode;
  }
  private htmlPreviewDebounceTimer: any;

  currentStep = 0;
  selectedSeries;
  selectedProduct = '';
  selectedTheme;
  dealerId;
  selectedEditorType: 'new' | 'html' | null = null;

  /**
   * Home page flow. A home page is an ordinary HTML page carrying is_home=1: the
   * storefront serves its markup on the store root (https://<store>/<country>/<lang>)
   * in place of the built-in home page, instead of at /page/<slug>. Only the HTML
   * editor produces a standalone document, so the page type pins the editor to
   * HTML and skips the product and editor steps.
   */
  isHomePage = false;

  /** Steps that make no sense for a home page — it is always a blank HTML page. */
  private readonly HOME_PAGE_SKIPPED_STEPS = ['Select Products', 'Select Editor'];

  customPageData: any = {
    store_id: 0,
    page_url: '',
    page_title: '',
    page_content: {},
    page_option: {
      show_header: false,
      show_footer: false,
      container_style: 1,
    },
    visibility: 1,
    page_theme: 1,
    metadata: {
      page_title: '',
      keywords: '',
      description: '',
      og_image: '',
    },
  };

  showLeaveConfirmation: boolean = false;
  country;
  filteredCountry: string = '';
  generateMethod = 1;
  dealer$ = this.dealerService.getDealer();
  getNewApi: any = null;

  constructor(
    private svc: StoreService,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private modalService: NgbModal,
    private utils: UtilitiesService,
    private dataService: DataService,
    private dealerService: DealerService,
  ) {}

  ngOnDestroy(): void {
    if (this.iframeMessageHandler) {
      window.removeEventListener('message', this.iframeMessageHandler);
    }
    if (this.themeObserver) {
      this.themeObserver.disconnect();
      this.themeObserver = null;
    }
  }

  async canDeactivate() {
    return await this.confirmLeaving();
  }

  async confirmLeaving() {
    if (this.showLeaveConfirmation) {
      // Ask for confirmation before leaving if showLeaveConfirmation is true
      return await this.openModal(this.returnConfirmModal).then(
        (response) => {
          if (response === 'save') {
            this.updateCustomPage(null, SAVE_BEHAVIOUR.SAVE);
            return false;
          } else if (response === 'discard' || response === 'cancel') {
            this.showLeaveConfirmation = false;
            return true;
          } else {
            return false;
          }
        },
        (reason) => {
              },
      );
    } else {
      return true;
    }
  }

  ngOnInit(): void {
    this.duplicateChecking = localStorage.getItem('duplicatePage');

    if (this.duplicateChecking === 'true') {
      this.duplicateChecking = false;
      localStorage.setItem('duplicatePage', this.duplicateChecking);
      setTimeout(() => {
        this.utils.notify.showSuccess(
          this.utils.translate('Page has been successfully duplicated!'),
        );
      }, 1000);
    }
    this.bearer = localStorage.getItem('bearer');
    this.storeId = localStorage.getItem('store_id');
    const info = JSON.parse(localStorage.getItem('5s-info'));
    this.dealerId = info.d_id;
    this.getDealerInfo();

    this.customPageData['store_id'] = this.storeId;

    // Setup iframe message listener on component initialization
    this.setupIframeMessageListener();

    // Watch for dark/light theme changes and notify iframe
    this.setupThemeObserver();

    const langCode = this.activeRoute.snapshot.params.langCode ?? 'en-US';
    const pageId = this.activeRoute.snapshot.params.id;
    this.storePageId = pageId;
    this.language_code = langCode;

    if (pageId) {
      this.isValidUrl = true;
      this.pageMode = PAGE_MODE.EDIT;
      this.generateProductNamesInSeries();
      this.getCustomPage(pageId);
    } else {
      this.pageMode = PAGE_MODE.CREATE;
      this.getConfigOG();

      // Arriving from Website > Design, which offers the home page on its own and
      // has already established the store has none to edit. That card is the only
      // entry point for a home page; Pages > Create Page never makes one.
      if (this.activeRoute.snapshot.queryParams.type === 'home') {
        this.applyHomePageType();
      }
    }
    this.fetchLanguage();
    this.fetchCountry();
  }

  setNewPageGenerateMethod(value) {
    if (this.pageMode === PAGE_MODE.EDIT) return;
    // A home page is always written by hand in the HTML editor — its product and
    // editor steps are hidden and applyHomePageType pins it to "custom". The plan
    // and country defaults that switch new pages to AI generation arrive after
    // that (dealer info and fetchCountry both resolve async), so without this they
    // would quietly undo it and the Design page guard would then demand a product.
    if (this.isHomePage) return;
    this.generateMethod = value;
  }

  getDealerInfo(): void {
    if (this.dataService.getDealerInfo() === null) {
      this.dealer$.subscribe((res) => {
        this.dataService.updateDealerInfo(res);
        this.dataService.updateDealerPlan(res?.dealer_plan);
        this.dataService.updateDealerCredits(res?.dealer_credits);
        this.dataService.updateDealerABList(res?.dealer_affiliate_buddy);
        this.applyDealerInfo(res);
      });
    } else {
      this.applyDealerInfo(this.dataService.getDealerInfo());
    }
  }

  private applyDealerInfo(info: any): void {
    this.featureList = info?.dealer_plan?.feature_list;
    this.checkByCredit =
      info?.dealer_credits?.totalCredit >=
        info?.dealer_credits?.minimumCredit &&
      info?.dealer_credits?.is_valid === true;
    this.plan = info?.dealer_plan?.plan_name?.toLowerCase();
    this.affiliateBuddyList = info?.dealer_affiliate_buddy;

    if (
      info?.dealer_plan?.feature_list?.includes('ai-page-template') &&
      this.checkByCredit === true
    ) {
      this.enableAIPageTemplate = true;
      this.setNewPageGenerateMethod(0);
    }
  }

  getAffiliateList() {
    this.svc.getAffiliateList(this.bearer, this.storeId).subscribe(
      (res: any) => {
        this.dataService.updateDealerABList(res['data']);
        this.affiliateBuddyList = res['data'];
      },
      (error) => {
        const errorMsg = this.utils.getErrorMessageWithRefId(
          error,
          'Unknown error, kindly contact our eStore team to check'
        );
        this.utils.notify.showError(errorMsg, 'Error!');
      }
    );
  }

  getSeries(countryCode = '') {
    combineLatest([
      this.svc.getProductSeriesList(countryCode),
      this.svc.getProductNameBySeries(countryCode),
    ]).subscribe(
      (res: any[]) => {
        const productSeries = res[0];
        const productNameBySeries = res[1];

        this.productSeries = productSeries?.data;
        this.productNameBySeries = productNameBySeries?.data;
      },
      () => {},
    );
  }

  /**
   * DEV / DEBUG: snapshot of the wizard/form state for the <app-debug-state> panel.
   * Kept to the fields that actually drive the wizard so it stays readable.
   */
  get debugState() {
    return {
      pageMode: this.pageMode,
      currentStep: this.currentStep,
      stepName: this.stepSchema?.[this.currentStep]?.name,
      subpage: this.subpage,
      filteredCountry: this.filteredCountry,
      generateMethod: this.generateMethod,
      enableAIPageTemplate: this.enableAIPageTemplate,
      isProductPageFlow: this.isProductPageFlow,
      htmlTemplatesLoaded: this.htmlTemplatesLoaded,
      selectedEditorType: this.selectedEditorType,
      selectedSeries: this.selectedSeries,
      selectedProduct: this.selectedProduct,
      selectedProductKey: this.selectedProductKey,
      selectedTheme: this.selectedTheme,
      selectedStudioTemplate: this.selectedStudioTemplate,
      studioTemplateSelected: this.studioTemplateSelected,
      selectedHtmlTemplate: this.selectedHtmlTemplate,
      htmlTemplateSelected: this.htmlTemplateSelected,
      isGeneratingProductHtml: this.isGeneratingProductHtml,
      progressValue: this.progressValue,
      activeStyleId: this.activeStyleId,
      activeTemplateStyles: this.activeTemplateStyles,
      readyToSave: this.readyToSave,
      hasUnsavedChanges: this.hasUnsavedChanges,
      customPageData: {
        page_title: this.customPageData?.page_title,
        page_url: this.customPageData?.page_url,
        page_theme: this.customPageData?.page_theme,
        visibility: this.customPageData?.visibility,
      },
    };
  }

  get isCreatePage() {
    return this.pageMode === PAGE_MODE.CREATE;
  }

  get isEditPage() {
    return this.pageMode === PAGE_MODE.EDIT;
  }

  // Studio is the visual editor on every platform since the Classic editor was
  // retired (it used to be hidden on estoreglobe / globemerce).
  get showStudioEditor() {
    return true;
  }

  get dashboardPageTitle() {
    return this.isCreatePage ? 'Create Custom Page' : 'Edit Custom Page';
  }

  prevStep() {
    const { currentStep, subpage } = this;

    // Locked while product HTML generates, same as nextStep().
    if (this.isGeneratingProductHtml) {
      return;
    }

    // HTML editor: from the code editor, go back to the template picker (stay on
    // the Design page step). Edit mode skips the picker, so it is excluded here.
    if (
      !this.isEditPage &&
      currentStep === 3 &&
      this.selectedEditorType === 'html' &&
      this.htmlTemplateSelected
    ) {
      this.htmlTemplateSelected = false;
      this.readyToSave = false;
      // Back to the picker — the toolbar (and its Styles dropdown) is gone, so drop
      // the group state rather than leave it pointing at a template we've left.
      this.activeTemplateStyles = [];
      this.activeStyleId = null;
      return;
    }

    // Home page: the Select Editor step it would otherwise land on is hidden, so
    // Back from the template picker returns to the name step.
    if (this.isHomePage && currentStep === this.designPageStepIndex) {
      this.gotoStep(0);
      return;
    }

    // HTML editor (edit mode): from the Design page (code editor or picker) go back
    // to the Select Editor step (editor-choice cards). gotoStep(2) restores the
    // editor view, so this reliably returns to the existing content.
    if (
      this.isEditPage &&
      currentStep === 3 &&
      this.selectedEditorType === 'html'
    ) {
      this.gotoStep(2);
      return;
    }

    // In edit mode, skip the Select Editor step (step 2) when going back
    if (this.isEditPage && currentStep === 3) {
      // Go back to Select Products (step 1) from Design page
      this.gotoStep(1);
      return;
    }

    // Studio editor: going back from Settings, return to content subpage
    if (currentStep === 4 && this.selectedEditorType === 'new') {
      this.currentStep = 3;
      this.subpage = 1;
      return;
    }

    if (subpage > 0) {
      this.gotoSubpage(subpage - 1);
    } else {
      if (!this.isFirstStep) {
        this.gotoStep(currentStep - 1);
      }
    }
  }

  nextStep() {
    const { currentStep, subpage } = this;
    // Product HTML generation is in flight — ignore Continue so a second request
    // can't be fired and the pending one can't be reassigned to another template.
    if (this.isGeneratingProductHtml) {
      return;
    }
    const enabledSiteNav = this.stepSiteNavs?.filter((nav: any) => {
      return nav.disabled !== true;
    });

    // Home page: Select Products and Select Editor are hidden (HTML editor, no
    // product), so Continue on the name step goes straight to the Design page.
    if (this.isHomePage && currentStep === 0) {
      const designPageIndex = this.designPageStepIndex;
      if (designPageIndex !== -1) {
        // Creating: land on the template picker, which is where the page's first
        // markup comes from. Editing: the page already has markup and the name step
        // is the only thing before the Design page (Select Editor is hidden), so
        // Continue reopens the code editor on that markup. Sending an edit to the
        // picker instead would make re-picking a template the only way forward.
        this.htmlTemplateSelected = this.isEditPage;
        this.gotoStep(designPageIndex);
        return;
      }
    }

    // In edit mode, skip the Select Editor step (step 2)
    if (this.isEditPage && currentStep === 1) {
      // Not get_new: on an existing page it overwrites the live Studio content
      // with the base theme's. The editor only needs to follow a slug change, so
      // that its next save lands on the page's new slug.
      if (
        this.selectedEditorType === 'new' &&
        this.customPageData.page_url !== this.currentEditPageUrl
      ) {
        this.buildIframeUrl(
          this.filteredCountry,
          this.language_code,
          this.customPageData.page_url,
          this.storeId,
        );
      }
      this.gotoStep(3);
      if (this.selectedEditorType === 'html') {
        setTimeout(() => this.reinitHtmlEditorIfNeeded(), 100);
      }
      return;
    }

    // Studio editor: go to design page step (template selection) without calling API yet.
    // Keep studioTemplateSelected false so gotoStep(3) doesn't auto-skip subpage 0 —
    // the user should land on the template selection page. selectedStudioTemplate is
    // preserved so a previously chosen card stays visually highlighted; the Continue
    // gate at subpage 0 reads selectedStudioTemplate directly, so the flow works
    // without forcing the user to re-click the same card.
    if (currentStep === 2 && this.selectedEditorType === 'new') {
      this.studioTemplateSelected = false;
      const designPageIndex = this.stepSchema.findIndex(
        (step) => step.name === 'Design page',
      );
      if (designPageIndex !== -1) {
        this.gotoStep(designPageIndex);
      }
      return;
    }

    // HTML editor selected: skip to Design page
    if (currentStep === 2 && this.selectedEditorType === 'html') {
      const designPageIndex = this.stepSchema.findIndex(
        (step) => step.name === 'Design page',
      );
      if (designPageIndex !== -1) {
        // Land on the HTML template picker first; the code editor opens once a
        // template is chosen (see the currentStep === 3 handler below).
        this.htmlTemplateSelected = false;
        if (this.isEditPage) {
          // Snapshot the current content (restored on a "keep current" Continue) and
          // pre-select the template this page was built from, read from its embedded
          // marker. No marker → selectedHtmlTemplate stays null → "Current page".
          this.editHtmlSnapshot = this.htmlEditorContent;
          this.applyEditTemplatePreselect();
        }
        this.gotoStep(designPageIndex);
        return;
      }
    }

    // HTML editor: from the template picker, load the chosen template into the editor.
    if (
      currentStep === 3 &&
      this.selectedEditorType === 'html' &&
      !this.htmlTemplateSelected
    ) {
      // Edit mode: keep the current page UNLESS the user actively switched to a
      // different template. "Current page" (no selection) or the page's own template
      // left unchanged both reopen the editor with the existing content — reinit
      // straight from htmlEditorContent (no re-dispatch) so the change listener doesn't
      // spuriously mark the page dirty. Only a genuinely different template reloads.
      if (this.isEditPage) {
        const chosen = this.selectedHtmlTemplate ?? null;
        const original = this.editHtmlTemplateId ?? null;
        if (!chosen || chosen === original) {
          const unsaved = this.hasUnsavedChanges;
          this.htmlEditorContent =
            this.editHtmlSnapshot ?? this.htmlEditorContent ?? '';
          this.htmlTemplateSelected = true;
          this.readyToSave = true;
          setTimeout(() => {
            this.reinitHtmlEditorIfNeeded();
            this.updateHtmlPreview();
            this.hasUnsavedChanges = unsaved;
          }, 100);
          return;
        }
        this.openSelectedHtmlTemplate();
        return;
      }
      if (!this.selectedHtmlTemplate) {
        this.utils.notify.showWarning(
          'Please select a template before continuing!',
        );
        return;
      }
      this.openSelectedHtmlTemplate();
      return;
    }

    // Studio editor (edit mode): the page already exists, so applying a template must
    // not run the create-flow get_new call — see applyStudioTemplateToEditor() for why
    // that both repointed page_url and left Continue looking dead.
    if (
      this.isEditPage &&
      currentStep === 3 &&
      this.selectedEditorType === 'new' &&
      subpage === 0
    ) {
      if (!this.selectedStudioTemplate) {
        this.utils.notify.showWarning('Please select a template before continuing!');
        return;
      }
      this.studioTemplateSelected = true;
      this.subpage = 1;
      this.applyStudioTemplateToEditor();
      return;
    }

    // Studio editor: when on template subpage, switch to content tab then call API.
    // Gate on selectedStudioTemplate (the visual selection) rather than the
    // studioTemplateSelected flag, which gets reset when navigating in via
    // Select Editor → Continue but the card still shows as highlighted.
    if (currentStep === 3 && this.selectedEditorType === 'new' && subpage === 0) {
      if (!this.selectedStudioTemplate) {
        this.utils.notify.showWarning('Please select a template before continuing!');
        return;
      }
      this.studioTemplateSelected = true;
      this.subpage = 1;
      this.callCreateNewCustomPageAndGotoDesign();
      return;
    }

    // Studio editor: when on content subpage, post to iframe to get payload, then call create_from_json and go to Settings
    if (currentStep === 3 && this.selectedEditorType === 'new' && subpage === 1) {
      this.studioContinueToSettings = true;
      this.postMessageToIframe({
        app: 'eStore',
        action: 'saveBtnCall',
      });
      return;
    }

    if (subpage + 1 < enabledSiteNav.length) {
      if (subpage === 1) {
        this.gotoStep(currentStep + 1);
      } else {
        this.gotoSubpage(subpage + 1);
      }
    } else {
      if (!this.isFinalStep) {
        this.gotoStep(currentStep + 1);
      }
    }
  }

  openModal(content) {
    return this.modalService.open(content, {
      ariaLabelledBy: 'modal-basic-title',
      size: 'sm',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'modal-customer',
      centered: true,
    }).result;
  }

  testGuardForStep(step) {
    const stepGuard = this.stepSchema[step].guard;
    if (stepGuard && typeof stepGuard === 'function') {
      // Step has guard
      const result = stepGuard() ?? [];
      if (result?.length) {
        return typeof result === 'object' ? result : [result];
      }
    } else {
      //  Step has no guard
    }
    return [];
  }

  subpage = 0;
  studioTemplateSelected = false;
  selectedStudioTemplate: string | number = null;
  studioSavedPayload: any = null;
  studioContinueToSettings = false;
  // A create_from_json is running (see saveStudioSections); the editor's
  // answers to further save clicks are dropped meanwhile
  private studioSectionsSaving = false;
  // A page made in the retired Classic editor, opened in Studio after being
  // converted (classic-to-studio). Until its first save it is still a classic
  // page on the server: that save switches it over (saveClassicUpgrade).
  isClassicUpgrade = false;
  classicUpgradeReport: ClassicConversionReport = null;
  // Continue on the Design page holds the edited sections here until Save, so
  // leaving the wizard halfway changes nothing on the live page.
  private pendingUpgradePayload: any = null;
  // The converted page's classic theme, kept in page_option for a restore
  private classicPageTheme: number = null;
  // The theme a converted page switches to: the Studio base theme, the one
  // page/theme/get_new gives every new Studio page
  private studioBaseThemeId: number = null;
  // A Studio page saved with page_theme -1 (getDefaultTemplate used to reset
  // it on edit): page/details then serves page_content, a classic backup on an
  // upgraded page, instead of its sections. Its next save restores the theme.
  private lostStudioTheme = false;
  // What each conversion warning tells the dealer (English keys of translation.json)
  readonly classicUpgradeWarningText: { [warning: string]: string } = {
    row_scripts:
      'Some HTML blocks run scripts (sliders, accordions). They run on your published page, not in this preview.',
    custom_js: "The page's custom JavaScript has moved to Settings.",
    custom_css_dropped:
      "The page's custom CSS styled parts of the classic layout that are now editable sections, so it was not carried over.",
    device_only_rows:
      'Rows shown only on mobile or only on desktop are kept as HTML blocks, so they still show only there.',
    rows_not_published: 'Rows that were never published were left out.',
    missing_design:
      'The classic layout data was missing, so every row is kept as an HTML block.',
  };
  // Data-driven Studio starter templates (status=2 rows, e.g. migrated
  // "Affiliate Buddy" templates). selectedStudioTemplateData holds the chosen
  // template's section array to seed the editor via the iframe init message.
  studioTemplateList: any[] = [];
  selectedStudioTemplateData: any = null;
  // Category filter over the Studio starter templates. The option list is
  // derived from the categories the rows actually carry (see
  // studioDropdownCategoryList), so it needs no hardcoded names.
  // displayedStudioTemplateList is what the grid renders.
  studioPageFilter = 'All';
  displayedStudioTemplateList: any[] = [];

  async gotoStep(nextStep) {
    const { currentStep } = this;

    // In edit mode, skip the Select Editor step (step 2) — except for HTML pages,
    // where it's exposed so the user can reopen the template picker and re-pick.
    if (this.isEditPage && this.selectedEditorType !== 'html') {
      if (nextStep === 2) {
        // If trying to go to Select Editor step, redirect to Design page instead
        nextStep = 3;
      } else if (nextStep > 2) {
        // Adjust step indices for steps after Select Editor
        // No adjustment needed as we're just skipping step 2
      }
    }

    // If going to previous step
    if (nextStep < currentStep) {
      // Check if leaving is permitted, if not, prevent leaving
      if (!(await this.confirmLeaving())) {
        return;
      }
    } else {
      // Only validate beforeContinue when moving forward
      const currentStepData = this.stepSchema[currentStep];
      if (typeof currentStepData?.beforeContinue === 'function') {
        const beforeContinueResult = await currentStepData.beforeContinue();
        if (!beforeContinueResult) {
          // beforeContinue returns false
          return;
        }
      }
    }

    // Note: Editor selection redirect is handled in nextStep() method

    const guardResult = this.testGuardForStep(nextStep);
    if (guardResult?.length) {
      this.utils.notify.showWarning(guardResult);
      return false;
    }
    // Studio editor: go to content subpage if template already loaded, skip reload
    if (this.selectedEditorType === 'new' && nextStep === 3 && this.studioTemplateSelected) {
      this.subpage = 1;
      this.currentStep = +nextStep;
      return;
    }

    this.subpage = 0;
    this.gotoSubpage(this.subpage, nextStep);

    const stepData = this.stepSchema[nextStep];
    if (typeof stepData?.onEnter === 'function') {
      stepData.onEnter();
    }
    this.currentStep = +nextStep;

    // Edit mode, HTML: entering the Select Editor (editor-choice cards) step.
    // Restore the editor view (htmlTemplateSelected=true) so the Design page tab
    // reloads the existing page content rather than the picker — the picker is only
    // reached by actively choosing the HTML card → Continue (which sets it false).
    // Also preload the templates so that picker isn't empty. Both are idempotent.
    if (
      this.isEditPage &&
      +nextStep === 2 &&
      this.selectedEditorType === 'html'
    ) {
      this.htmlTemplateSelected = true;
      this.loadHtmlTemplatesFromApi();
    }

    // Reinit HTML editor when navigating to Design page step
    if (+nextStep === 3 && this.selectedEditorType === 'html') {
      setTimeout(() => this.reinitHtmlEditorIfNeeded(), 100);
    }
  }

  async gotoSubpage(index, parentPage = null) {
    const { subpage: currentSubpage } = this;
    const subpage = index ?? 0;
    if (subpage == 1 && currentSubpage == 2) {
      this.showLeaveConfirmation = false;
    }
    if (subpage < currentSubpage && parentPage === null) {
      // Check if leaving is permitted, if not, prevent leaving
      if (!(await this.confirmLeaving())) {
        return;
      }
    }

    let subpageData = this.stepSiteNavs[subpage];
    if (parentPage) {
      subpageData = (this.stepSchema[parentPage]?.sidenav ?? [])[subpage];
    }

    if (subpageData) {
      // TODO guard logic
      // Same classic-only check as the Settings guard: Studio has no
      // selectedTheme, and its Content tab is already gated by the side-nav's
      // disabled: !studioTemplateSelected. Without this exemption, clicking
      // Content on a Studio page in edit mode was refused with a warning.
      if (subpage === 1 && this.selectedEditorType !== 'new') {
        if (!this.selectedTheme) {
          this.utils.notify.showWarning(
            'Please select page template before continue',
          );
          return false;
        }
      }

      if (typeof subpageData?.onEnter === 'function') {
        subpageData.onEnter();
      }
      this.subpage = subpage;
    }
  }

  get canContinue() {
    const guardMsg = this.testGuardForStep(this.currentStep + 1);
    return !guardMsg?.length;
  }

  get isStepSitenavAvailable() {
    return !!this.stepSiteNavs?.length;
  }

  convertToDataURI(svg: string) {
    const encodedSvg = encodeURIComponent(svg);
    const svgUrl = `data:image/svg+xml;charset=utf-8,${encodedSvg}`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(svgUrl);
  }

  get stepSiteNavs() {
    // HTML editor has no sub-navigation — the template picker and the code editor
    // are a single linear flow (Continue advances picker -> editor).
    if (this.selectedEditorType === 'html' && this.isCurrentStep(3)) {
      return [];
    }
    // Show only template + content (no shortcode) for studio editor
    if (this.selectedEditorType === 'new' && this.isCurrentStep(3)) {
      const sidenav = this.stepSchema[this.currentStep]?.sidenav ?? [];
      return sidenav
        .filter((nav) => nav.id === 'template' || nav.id === 'content')
        .map((nav) => {
          if (nav.id === 'content') {
            return {
              ...nav,
              disabled: !this.studioTemplateSelected,
              hideTitle: true,
              onEnter: () => {},
            };
          }
          return nav;
        });
    }
    return this.stepSchema[this.currentStep]?.sidenav ?? [];
  }

  isCurrentStep(step) {
    return +this.currentStep === +step;
  }

  isPrevStep(step) {
    return +this.currentStep > +step;
  }

  isNextStep(step) {
    return +this.currentStep < +step;
  }

  isGotoAllowed(step) {
    // No step jumps while product HTML generates — same lock as next/prevStep.
    if (this.isGeneratingProductHtml) {
      return false;
    }
    // In edit mode, don't allow navigation to Select Editor step (step 2) — except
    // for HTML pages, where the tab is exposed so the user can re-pick the template.
    if (this.isEditPage && step === 2 && this.selectedEditorType !== 'html') {
      return false;
    }
    return (
      this.isPrevStep(step) || (this.isEditPage && !this.isCurrentStep(step))
    );
  }

  onGenerateMethodChange() {
    this.getDefaultTemplate();

    // Reset editor selection when changing generate method (not in edit mode)
    if (!this.isEditPage) {
      this.selectedEditorType = null;
    }
  }

  onLanguageChange(languageCode, languageName) {
    this.language_code = languageCode;
    this.language_name = languageName;
  }

  isABSelected(option: any): boolean {
    return this.selectedABOptions.includes(option);
  }

  toggleABSelection(option: any): void {
    const index = this.selectedABOptions.indexOf(option);
    if (index !== -1) {
      this.selectedABOptions.splice(index, 1);
    } else {
      this.selectedABOptions.push(option);
    }
  }

  getSelectedABOptionsText(): string {
    if (this.selectedABOptions.length === 0) {
      return this.utils.translate('Select Affiliate Buddy');
    }
    return (
      this.selectedABOptions.length + ' ' + this.utils.translate('selected')
    );
  }

  fetchLanguage() {
    const store_id = localStorage.getItem('store_id');
    this.svc.getStoreLanguage(this.bearer, store_id).subscribe(
      (res) => {
        let languages = [];
        res['data']?.forEach((lang) => {
          if (+lang.status) {
            languages.push(lang);
          }
        });
        this.languages = [...languages];

        const defaultLanguage =
          languages.find((lang) => +lang.default === 1) ?? languages[0];

        this.onLanguageChange(
          defaultLanguage?.language_code,
          defaultLanguage?.language_name,
        );
      },
      (error) => {},
    );
  }

  fetchCountry() {
    // Mark loading from the very first request so the step-1 inputs are disabled
    // (with spinner) immediately, not only once changeCountry() runs. changeCountry
    // keeps it true through the series/theme fetch and clears it when done.
    this.changeCountryIsLoading = true;
    this.svc.getCountry(this.bearer).subscribe(
      (res) => {
        if (res['data']) {
          this.country = res['data'];
          const defaultCountry = this.country?.find((c) => c.default === '1');
          this.filteredCountry = defaultCountry?.country_code ?? '';
          this.changeCountry(this.filteredCountry);
        } else {
          // No country data — don't leave the inputs locked.
          this.changeCountryIsLoading = false;
        }
      },
      () => {
        this.changeCountryIsLoading = false;
      },
    );
  }

  getCountryName(country_code: string) {
    country_code = (country_code || '').toLowerCase();
    const country = this.country?.find(
      (c) => (c.country_code || '').toLowerCase() === country_code,
    );
    return country?.country_name ?? country_code;
  }

  changeCountry(countryCode: string, reset: boolean = true) {
    this.changeCountryIsLoading = true;

    this.filteredCountry = countryCode;
    this.productSeries = [];
    this.getSeries(countryCode);
    if (reset) {
      this.resetSeries();
    }
    this.showPageThemeSelector(countryCode).then(() => {
      this.getDefaultTemplate();
      this.changeCountryIsLoading = false;
    });
    if (
      countryCode === 'TWTW' ||
      (this.plan !== 'basic' && this.checkByCredit === true)
    ) {
      this.enableAIPageTemplate = true;
      this.setNewPageGenerateMethod(0);
    } else {
      this.enableAIPageTemplate = false;
      this.setNewPageGenerateMethod(1);
    }
  }

  resetSeries() {
    this.selectedSeries = '';
    this.selectedProduct = '';
  }

  getDefaultTemplate() {
    // A Studio page's theme is the Studio one page/theme/get_new or the classic
    // upgrade gave it, and page/details serves the sections only while it holds.
    // Edit mode reaches here once changeCountry's theme list loads, after the
    // page has opened in Studio; resetting to -1 then made the next save turn
    // the page classic again, and an upgraded page served its classic backup.
    if (this.selectedEditorType === 'new') {
      return;
    }
    if (this.filteredCountry !== '') {
      if (+this.generateMethod === 1) {
        this.selectedTheme = -1;
      } else {
        if (
          this.selectedTheme === '' ||
          this.selectedTheme === undefined ||
          this.selectedTheme === -1
        ) {
          let theme = this.pageThemeList?.find(
            (t) => t.country_code.toUpperCase() === this.filteredCountry,
          );

          if (theme === undefined) {
            theme = this.pageThemeList[0];
          }
          this.selectedTheme = theme.store_page_theme_id;
        }
      }
    }
  }

  public validatePageUrl() {
    return new Promise((resolve, reject) => {
      const url = this.customPageData.page_url;

      // Nothing to check for a home page: there is no URL field, and the slug the
      // backend files it under is picked to be free (Page::availableHomePageUrl).
      if (this.isHomePage) {
        this.isValidUrl = true;
        resolve(true);
        return;
      }

      if (url !== '') {
        if (url !== this.currentEditPageUrl) {
          this.validatingURL = true;
          this.svc.validatePageUrl(url).subscribe(
            (res) => {
              this.validatingURL = false;
              this.isValidUrl = true;
              resolve(true); // Success
            },
            (error) => {
              this.validatingURL = false;
              this.isValidUrl = false;
              this.utils.notify.showError('Page URL has been used!');
              reject(false); // Fail
            },
          );
        } else {
          this.isValidUrl = true;
          resolve(this.isValidUrl);
        }
      } else {
        resolve(this.isValidUrl); // Fail
      }
    });
  }

  onSelectSeries(seriesName) {
    // Reset selected product
    this.selectedProduct = '';
    this.selectedSeries = seriesName;
    this.generateProductNamesInSeries(seriesName);
  }

  onSelectProduct(productName) {
    this.selectedProduct = productName.name;
    this.selectedProductKey = productName.product_content_key;
  }

  onSelectEditor(editorType: 'new' | 'html') {
    this.selectedEditorType = editorType;
    this.showPreviewButton = editorType !== 'new';
    if (editorType === 'html') {
      this.loadHtmlTemplatesFromApi();
    }
    if (editorType === 'new') {
      this.loadStudioTemplates();
    }
  }

  /**
   * Turn this into the home page flow. A home page is HTML-only, so the editor is
   * pinned here and the two steps that would ask for it again are hidden (see
   * isStepVisible). There is no page type to pick: Website > Design is the only
   * way in, and Website > Pages > Create Page makes landing pages only.
   */
  private applyHomePageType() {
    this.isHomePage = true;
    this.markAsChanged();

    this.selectedEditorType = 'html';
    this.showPreviewButton = true;
    // A home page is never generated from a product, so it always takes the
    // "custom" path (no product selection, no AI generation).
    this.generateMethod = 1;
    this.loadHtmlTemplatesFromApi();

    if (this.isCreatePage) {
      // The URL field is hidden for a home page and the backend assigns the slug,
      // so clear whatever was typed.
      this.customPageData.page_url = '';

      if (!this.customPageData.page_title) {
        this.customPageData.page_title = this.utils.translate('Home Page');
      }
    }
  }

  /**
   * Whether a step appears in the step indicator. Steps are hidden rather than
   * removed so the indices the flow is written against (Design page = 3) hold for
   * every combination of editor and page type.
   */
  isStepVisible(stepItem: any): boolean {
    // Edit mode opens the page in the editor it was built with, so there is
    // nothing to choose — except for HTML pages, where the step doubles as the
    // way back into the template picker.
    if (
      this.isEditPage &&
      this.selectedEditorType !== 'html' &&
      stepItem.name === 'Select Editor'
    ) {
      return false;
    }

    // The HTML editor carries its own settings (metadata lives in the markup).
    if (this.selectedEditorType === 'html' && stepItem.name === 'Settings') {
      return false;
    }

    return !(
      this.isHomePage && this.HOME_PAGE_SKIPPED_STEPS.includes(stepItem.name)
    );
  }

  /**
   * Where leaving the editor lands — the back arrow, and a finished save. A home
   * page is not listed under Pages; it is managed from Website > Design, so both
   * exits have to go where the page can actually be seen.
   */
  get exitRoute(): string {
    return this.isHomePage ? '/dashboard/design' : '/dashboard/pages';
  }

  /** Index of the step the home page flow continues to from the name step. */
  private get designPageStepIndex(): number {
    return this.stepSchema.findIndex((step) => step.name === 'Design page');
  }

  /**
   * Sub-heading of the current step. The name step's stock copy asks for a name
   * *and a URL*, which a home page has no field for.
   */
  get currentStepSubtext(): string {
    if (this.isHomePage && this.currentStep === 0) {
      return `Name your home page. Once saved, it takes over your storefront's home page.`;
    }

    return this.stepSchema[this.currentStep]?.subtext;
  }

  /** Fetch the Studio starter templates (status=2 rows) for the picker. */
  loadStudioTemplates() {
    const countryCode = this.filteredCountry || '';
    this.svc.getStudioPageThemeList(countryCode).subscribe(
      (res: any) => {
        const rows = Array.isArray(res?.data) ? res.data : [];
        this.studioBaseThemeId = this.pickStudioBaseTheme(rows);
        // Only offer the migrated brand templates (Femoire/Sahiyya/Aemis, which
        // carry a real product_series). Exclude the generic base Studio template
        // ("New Landing Page", product_series 'ALL') — it's the internal base
        // used to seed new pages, not a user-facing starter.
        this.studioTemplateList = rows.filter((t: any) => {
          const series = (t?.product_series ?? '').toString().trim().toUpperCase();
          return series && series !== 'ALL';
        });
        // Reset to "All" so the grid isn't left on a filter with no matches.
        this.studioPageFilter = 'All';
        this.displayedStudioTemplateList = [...this.studioTemplateList];
      },
      () => {
        this.studioTemplateList = [];
        this.displayedStudioTemplateList = [];
      },
    );
  }

  /**
   * The Studio base theme among the Studio (status 2) theme rows: "New Landing
   * Page" (product_series 'ALL'), else the lowest id, as get_new picks it.
   */
  private pickStudioBaseTheme(rows: any[]): number {
    const base =
      rows.find(
        (t: any) => (t?.product_series ?? '').toString().trim().toUpperCase() === 'ALL',
      ) ??
      rows
        .slice()
        .sort((a: any, b: any) => +a?.store_page_theme_id - +b?.store_page_theme_id)[0];
    return base ? +base.store_page_theme_id || null : null;
  }

  onSelectStudioTemplate(template: string) {
    if (this.selectedStudioTemplate === template) {
      this.selectedStudioTemplate = null;
      this.studioTemplateSelected = false;
    } else {
      this.selectedStudioTemplate = template;
      this.studioTemplateSelected = true;
    }
    // Product / Blank start from the editor's own default content.
    this.selectedStudioTemplateData = null;
  }

  /**
   * Select a data-driven Studio starter template. Parses its section array so
   * onIframeLoad() can seed the editor via the init message's template_data.
   */
  onSelectStudioTheme(theme: any) {
    const id = theme?.store_page_theme_id;
    if (this.selectedStudioTemplate === id) {
      this.selectedStudioTemplate = null;
      this.studioTemplateSelected = false;
      this.selectedStudioTemplateData = null;
      return;
    }
    this.selectedStudioTemplate = id;
    this.studioTemplateSelected = true;
    try {
      this.selectedStudioTemplateData =
        typeof theme?.theme_template_json === 'string'
          ? JSON.parse(theme.theme_template_json)
          : theme?.theme_template_json ?? null;
    } catch {
      this.selectedStudioTemplateData = null;
    }
  }

  /** Highlight (or toggle off) an HTML starter template on the picker card. */
  onSelectHtmlTemplate(templateId: string) {
    // Locked while product HTML is generating — see isGeneratingProductHtml.
    if (this.isGeneratingProductHtml) {
      return;
    }
    this.selectedHtmlTemplate =
      this.selectedHtmlTemplate === templateId ? null : templateId;
  }

  /**
   * Switch the editor to another Style within the open template's group — same
   * behaviour in every flow (product and customize-myself): fetch the chosen
   * style's stored HTML (page/theme/content, no AI round-trip) and graft the
   * editor's current body onto its shell (see mergeStyleWithCurrentBody), so
   * generated content and manual edits survive the switch.
   *
   * Reverts the <select> to activeStyleId when the style can't be loaded.
   */
  onSelectStyle(styleId: number | string) {
    if (this.isGeneratingProductHtml || this.isLoadingSelectedTheme) {
      return;
    }
    // The active style is disabled in the dropdown, but ngModelChange can still
    // echo it back (e.g. the revert below) — ignore a no-op switch.
    if (styleId == null || String(styleId) === String(this.activeStyleId)) {
      return;
    }
    const previousStyleId = this.activeStyleId;
    this.activeStyleId = styleId;
    this.isLoadingSelectedTheme = true;
    this.svc.getPageThemeContent(styleId).subscribe(
      (res: any) => {
        this.isLoadingSelectedTheme = false;
        const styleHtml = String(res?.data?.content ?? '');
        if (!styleHtml.trim()) {
          this.activeStyleId = previousStyleId;
          this.utils.notify.showError(
            'That style has no content. Keeping the current style.',
          );
          return;
        }
        // Keep the generated copy, take the new style's shell.
        const merged = this.mergeStyleWithCurrentBody(styleHtml);
        this.applyHtmlTemplateContent(merged);
      },
      () => {
        this.isLoadingSelectedTheme = false;
        this.activeStyleId = previousStyleId;
        this.utils.notify.showError('Unable to load the selected style.');
      },
    );
  }

  /**
   * Graft the body the user already has onto a different style's shell.
   *
   * Switching Style must not re-run the LLM, so the body stays constant: the whole
   * <body>…</body> element from the editor's current content — the AI-generated
   * markup plus any edits the user has since made — replaces the new style's body,
   * while the style supplies everything around it (<head>, <style>, <script>, the
   * <html> wrapper).
   *
   * The <body> TAG travels with the content, not the shell, so a generated
   * `<body class="x">` keeps its attributes. This mirrors PageThemeLogic's
   * generateProductHtml(), which splices the same `<body\b[^>]*>.*</body>` region
   * back into the template wrapper after generation.
   *
   * Falls back to the style's HTML untouched when either side has no <body> (a
   * fragment template, say) — there is then nothing meaningful to transplant.
   *
   * @param styleHtml the newly selected style's full HTML document
   * @returns the style's document with the current body element spliced in
   */
  private mergeStyleWithCurrentBody(styleHtml: string): string {
    const currentBody = this.extractBodyElement(this.htmlEditorContent || '');
    if (currentBody === null) {
      return styleHtml;
    }
    // Whole-element match, same region the backend replaces. Non-greedy would stop
    // at a nested "</body>" inside a script/string, so keep it greedy like the PHP.
    const bodyPattern = /<body\b[^>]*>[\s\S]*<\/body\s*>/i;
    if (!bodyPattern.test(styleHtml)) {
      return styleHtml;
    }
    // Replace via a function so `$&`/`$1` sequences inside the carried-over HTML
    // aren't interpreted as replacement backreferences (the PHP escapes them for
    // the same reason).
    return styleHtml.replace(bodyPattern, () => currentBody);
  }

  /**
   * The complete <body>…</body> element of a document, tag and attributes included,
   * or null when it has no body.
   *
   * Deliberately string-based rather than DOMParser: the body must come back byte
   * for byte, and re-serialising through the DOM would normalise attribute quoting,
   * drop comments outside the body's children and rewrite entities.
   */
  private extractBodyElement(html: string): string | null {
    if (!html || !html.trim()) {
      return null;
    }
    const match = html.match(/<body\b[^>]*>[\s\S]*<\/body\s*>/i);
    return match ? match[0] : null;
  }

  /**
   * Cancel the product-generation progress animation and unlock the picker.
   * Leaves isLoadingSelectedTheme to the caller: the success path hands off to
   * progressCompleted(), which clears it once the bar reaches 100%.
   */
  private stopProductHtmlProgress() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.progressValue = 0;
    this.isGeneratingProductHtml = false;
  }

  /**
   * Edit mode: select the "Current page" option on the picker (no template id).
   * Continuing with this keeps the page's existing content instead of replacing it.
   */
  onKeepCurrentHtmlPage() {
    if (this.isGeneratingProductHtml) {
      return;
    }
    this.selectedHtmlTemplate = null;
  }

  /**
   * Edit mode: pre-select the template the current page was built from by reading the
   * embedded marker (see HTML_TEMPLATE_MARKER). Sets selectedHtmlTemplate to that id so
   * its card highlights once the list renders; leaves it null (→ "Current page") when
   * the page has no marker. Records the original id so a Continue that keeps the same
   * template is treated as "keep current" rather than a reload to the pristine template.
   */
  private applyEditTemplatePreselect() {
    const markerId = this.readTemplateMarker(this.htmlEditorContent);
    this.editHtmlTemplateId = markerId;
    this.selectedHtmlTemplate = markerId;
  }

  /** Read the embedded template-id marker from page content, or null if absent. */
  private readTemplateMarker(html: string): string | null {
    const m = (html || '').match(
      // `:` is part of grouped-card ids ('group:CODE'), hence [\w.:-].
      new RegExp('<!--\\s*' + this.HTML_TEMPLATE_MARKER + ':\\s*([\\w.:-]+)\\s*-->', 'i'),
    );
    return m ? m[1] : null;
  }

  /** Remove any existing template-id marker (and its line) from page content. */
  private stripTemplateMarker(html: string): string {
    return (html || '').replace(
      new RegExp(
        '[ \\t]*<!--\\s*' + this.HTML_TEMPLATE_MARKER + ':\\s*[\\w.:-]+\\s*-->[ \\t]*\\r?\\n?',
        'i',
      ),
      '',
    );
  }

  /**
   * Stamp the template id into page content as an HTML comment so edit mode can later
   * re-select it. Placed right after the doctype (never before it — leading content
   * trips browsers into quirks mode); prepended when there is no doctype. Strips any
   * prior marker first so re-applying a template doesn't stack duplicates.
   */
  private injectTemplateMarker(html: string, templateId: string): string {
    const stripped = this.stripTemplateMarker(html || '');
    if (!templateId) {
      return stripped;
    }
    const marker = '<!-- ' + this.HTML_TEMPLATE_MARKER + ': ' + templateId + ' -->';
    const doctype = stripped.match(/<!doctype[^>]*>/i);
    if (doctype && doctype.index != null) {
      const at = doctype.index + doctype[0].length;
      return stripped.slice(0, at) + '\n' + marker + stripped.slice(at);
    }
    return marker + '\n' + stripped;
  }

  /**
   * Product page flow: the user picked the AI/product generate method and chose a
   * series + product. In the HTML editor this makes the selected template's HTML
   * generated per-product server-side (see openSelectedHtmlTemplate).
   */
  get isProductPageFlow(): boolean {
    return (
      +this.generateMethod === 0 &&
      !!this.selectedSeries &&
      !!this.selectedProduct
    );
  }

  /**
   * Picker cards that carry more than one Style — the group's variants are
   * swappable in the editor toolbar (see onSelectStyle). Listed in their own
   * section above the single-style ones.
   */
  get multiStyleHtmlTemplates(): HtmlPageTemplate[] {
    return this.htmlTemplates.filter((t) => (t.group?.styles?.length ?? 0) > 1);
  }

  /** Picker cards with exactly one layout — no Styles dropdown once opened. */
  get singleStyleHtmlTemplates(): HtmlPageTemplate[] {
    return this.htmlTemplates.filter(
      (t) => (t.group?.styles?.length ?? 0) <= 1,
    );
  }

  /**
   * Collapsed state of the template picker's sections, keyed by section id. Both
   * start expanded; collapsing hides that section's cards but keeps its header
   * (and count) visible so the section is still discoverable.
   */
  htmlTemplateSectionCollapsed: { [section: string]: boolean } = {
    multi: false,
    single: false,
  };

  /**
   * Expand/collapse a template picker section. A selected card inside the
   * section stays selected while hidden — the header's count keeps it visible
   * that the section still holds templates.
   */
  toggleHtmlTemplateSection(section: 'multi' | 'single') {
    // Locked while product HTML generates, same as the cards themselves.
    if (this.isGeneratingProductHtml) {
      return;
    }
    this.htmlTemplateSectionCollapsed[section] =
      !this.htmlTemplateSectionCollapsed[section];
  }

  /**
   * Load the HTML-editor starter templates from the backend. A single request
   * populates the picker cards; each template's HTML is fetched lazily on "Use"
   * (see openSelectedHtmlTemplate).
   *
   * The same list serves every wizard flow: any row tagged theme_categories_id=5
   * is an HTML-editor template regardless of store_page_theme.type. Requested
   * grouped so templates sharing a `group` collapse into one card carrying
   * group_details (ungrouped rows pass through unchanged); see
   * mapThemeListRowToHtmlTemplate.
   */
  loadHtmlTemplatesFromApi(): void {
    if (this.htmlTemplatesLoaded || this.isLoadingHtmlTemplates) {
      return;
    }
    this.isLoadingHtmlTemplates = true;
    this.svc
      .getHtmlPageThemeList(null, true)
      .subscribe(
        (res: any) => {
          const rows: any[] = res?.data ?? [];
          const tpls: HtmlPageTemplate[] = rows
            // category 5 is the single marker for HTML-editor templates.
            .filter((row: any) => this.isHtmlEditorTheme(row))
            // Base order = insertion order (by id); Blank is hoisted to the front below.
            .sort(
              (a: any, b: any) =>
                (Number(a.store_page_theme_id) || 0) -
                (Number(b.store_page_theme_id) || 0),
            )
            .map((row: any) => this.mapThemeListRowToHtmlTemplate(row));
          // Show the Blank starter first, then the rest in id order.
          tpls.sort((a, b) => (a.isBlank ? 0 : 1) - (b.isBlank ? 0 : 1));
          // Drop a stale selection that no longer exists in the list.
          if (
            this.selectedHtmlTemplate &&
            !tpls.some((t) => t.id === this.selectedHtmlTemplate)
          ) {
            this.selectedHtmlTemplate = null;
          }
          this.htmlTemplates = tpls.length ? tpls : [this.blankStarterTemplate()];
          this.htmlTemplatesLoaded = true;
          this.isLoadingHtmlTemplates = false;
        },
        () => {
          this.isLoadingHtmlTemplates = false;
          // htmlTemplatesLoaded stays false so a later visit retries the list.
          this.htmlTemplates = [this.blankStarterTemplate()];
          this.utils.notify.showError(
            'Unable to load page templates. Please try again later.',
          );
        },
      );
  }

  /**
   * A Blank starter built locally, for when the backend offers no templates.
   *
   * Blank normally arrives like any other card -- a store_page_theme row named
   * "blank" -- and is hoisted to the front of the list. When the list comes back
   * empty, or does not come back at all, the picker would otherwise show
   * "No templates available." and the wizard would dead-end there: the home page
   * flow has no other way past this step. Blank is the one card that needs
   * neither stored HTML nor a backend row, since openSelectedHtmlTemplate()
   * short-circuits on isBlank and opens the editor with empty content.
   *
   * A fresh object each call, so nothing cached onto a card leaks into the next
   * one. No themeId: there is no row behind it to fetch.
   */
  private blankStarterTemplate(): HtmlPageTemplate {
    return {
      id: 'blank',
      name: 'Blank',
      description: 'Start from an empty page and write your own HTML.',
      fallsUnder: [],
      isBlank: true,
      icon: 'fi fi-rr-edit',
      content: '',
    };
  }

  /**
   * Build a picker card from a store_page_theme LIST row. The list response carries no
   * theme_template_json, so icon/description/tags aren't available here; the HTML
   * content is loaded lazily on selection (openSelectedHtmlTemplate) via themeId.
   */
  private mapThemeListRowToHtmlTemplate(row: any): HtmlPageTemplate {
    const isBlank =
      String(row?.theme_name ?? '').trim().toLowerCase() === 'blank';
    // theme_preview carries the Flaticon class for these rows (see the icon UPDATE
    // query); fall back to a generic icon if it isn't set or isn't a fi-* class.
    const preview = typeof row?.theme_preview === 'string' ? row.theme_preview : '';
    const icon = preview.indexOf('fi ') === 0 ? preview : 'fi fi-rr-file';

    // Grouped row (grouped=true): the card represents the whole group. Identify it by
    // the group code, label it with the group's name/description, and remember the
    // member ids so selecting the card loads the first one.
    const gd = row?.group_details;
    if (gd && gd.code) {
      const themeIds: Array<number | string> = Array.isArray(gd.theme_ids)
        ? gd.theme_ids
        : [row.store_page_theme_id];
      // group_details.theme_styles gives each member's own name so the editor can
      // offer the group's variants by label. Absent on older backends — fall back
      // to positional names over theme_ids so the ids stay usable either way.
      const styles: HtmlPageTemplateStyle[] = Array.isArray(gd.theme_styles)
        ? gd.theme_styles
            .filter((s: any) => s && s.id != null)
            .map((s: any, i: number) => ({
              id: s.id,
              name: String(s.name ?? '').trim() || `Style ${i + 1}`,
            }))
        : themeIds.map((id, i) => ({ id, name: `Style ${i + 1}` }));
      return {
        id: `group:${gd.code}`,
        name: gd.name || gd.code,
        description: gd.description || '',
        fallsUnder: [],
        isBlank: false,
        icon,
        content: '',
        themeId: themeIds[0] ?? row.store_page_theme_id,
        group: {
          code: gd.code,
          name: gd.name || gd.code,
          description: gd.description ?? null,
          themeIds,
          styles,
        },
      };
    }

    // String id (e.g. 'service') drives the SCSS thumbnail accent; fall back to the
    // numeric id for any template not in the map (renders a neutral thumbnail).
    const id =
      this.STORE_THEME_IDS[row.theme_name] || String(row.store_page_theme_id);
    return {
      id,
      name: row.theme_name || 'Untitled',
      description: '',
      fallsUnder: [],
      isBlank,
      icon,
      content: '',
      themeId: row.store_page_theme_id,
    };
  }

  /**
   * True when a store_page_theme row is an HTML-editor starter template — i.e. its
   * theme_categories_id includes 5 (rows may carry a comma-separated category list).
   */
  private isHtmlEditorTheme(row: any): boolean {
    if (!row) {
      return false;
    }
    return String(row.theme_categories_id ?? '')
      .split(',')
      .map((id) => id.trim())
      .includes(String(this.HTML_TEMPLATE_CATEGORY_ID));
  }

  /**
   * Map a store_page_theme row to the picker's HtmlPageTemplate. The descriptor —
   * including the editor content — lives in theme_template_json; flat columns are
   * fallbacks. Some rows (e.g. product HTML templates) may store raw HTML in
   * theme_template_json instead of the JSON envelope — those are wrapped as-is.
   * Returns null for unusable rows so they are skipped rather than rendered as
   * broken cards.
   */
  private mapThemeRowToHtmlTemplate(row: any): HtmlPageTemplate | null {
    if (!row?.theme_template_json) {
      return null;
    }
    let tpl: any;
    try {
      tpl =
        typeof row.theme_template_json === 'string'
          ? JSON.parse(row.theme_template_json)
          : row.theme_template_json;
    } catch {
      // Not JSON — product templates may carry the page HTML directly.
      const raw = String(row.theme_template_json).trim();
      if (raw.startsWith('<')) {
        return {
          id: `theme-${row.store_page_theme_id}`,
          name: row.theme_name || 'Untitled',
          description: '',
          fallsUnder: [],
          isBlank: false,
          icon: 'fi fi-rr-file',
          content: raw,
        };
      }
      return null;
    }
    if (!tpl || (!tpl.content && !tpl.isBlank)) {
      return null;
    }
    return {
      id: tpl.id || `theme-${row.store_page_theme_id}`,
      name: tpl.name || row.theme_name || 'Untitled',
      description: tpl.description || '',
      bestFor: tpl.bestFor,
      fallsUnder: tpl.fallsUnder || [],
      components: tpl.components || [],
      schema: tpl.schema || [],
      isBlank: tpl.isBlank || false,
      icon: tpl.icon || 'fi fi-rr-file',
      content: tpl.content || '',
    };
  }

  /**
   * Move from the HTML template picker to the code editor, loading the chosen
   * template's HTML into the editor.
   *
   * Product flow: the template's HTML is generated server-side — page/theme/get
   * sends the skeleton plus the selected product and language through Flowise and
   * returns the filled page (see middleware PageThemeLogic::generateProductHtml).
   * Generated content is product-specific, so it is never cached on the card
   * (cards are shared across products); instead StoreService keeps a session
   * cache keyed by the full generation params, so re-selecting the same
   * product + template serves the previous generation without another call.
   */
  private openSelectedHtmlTemplate() {
    const template = this.htmlTemplates.find(
      (t) => t.id === this.selectedHtmlTemplate,
    );
    if (!template) {
      return;
    }
    // Remember the group (if any) so the editor toolbar can offer its Styles and
    // swap between them; cleared for a standalone template.
    this.activeTemplateStyles = template.group?.styles?.length
      ? template.group.styles
      : [];
    this.activeStyleId = this.activeTemplateStyles.length
      ? template.themeId ?? this.activeTemplateStyles[0].id
      : null;
    // Product generation only applies to the HTML editor in the product flow —
    // never the classic or studio editors. Any HTML template picked there is
    // filled per-product server-side (Blank skips generation below).
    const isProduct =
      this.selectedEditorType === 'html' && this.isProductPageFlow;
    // Blank (or an already-fetched non-product template) opens immediately.
    if (template.isBlank || (template.content && !isProduct)) {
      this.applyHtmlTemplateContent(template.content || '');
      return;
    }
    this.loadHtmlThemeContent(template, template.themeId, isProduct);
  }

  /**
   * Fetch one store_page_theme's HTML and load it into the editor.
   *
   * @param template the picker card being opened (used for the non-product cache)
   * @param themeId  which store_page_theme row to load — the card's own themeId
   *                 (a group card's representative member for grouped templates)
   * @param isProduct true when the HTML is generated per-product server-side
   */
  private loadHtmlThemeContent(
    template: HtmlPageTemplate,
    themeId: number | string,
    isProduct: boolean,
  ) {
    const lang = this.language_code || 'en-US';
    const country = isProduct ? this.filteredCountry?.toUpperCase() : 'ALL';
    // Generated product HTML is deterministic per (store, theme, language,
    // product, country) for a session — serve a previous generation instantly
    // instead of re-running Flowise (~15s) for the same inputs.
    if (isProduct) {
      const cached = this.svc.getCachedGeneratedPageTheme(
        this.storeId,
        themeId,
        lang,
        this.selectedProduct,
        country,
      );
      if (cached) {
        const full = this.mapThemeRowToHtmlTemplate(cached?.data);
        this.applyHtmlTemplateContent((full && full.content) || '');
        return;
      }
    }
    // The list response carries no HTML — fetch the chosen template's content,
    // parse its theme_template_json and load it into the editor.
    this.isLoadingSelectedTheme = true;
    // Product generation runs the skeleton through Flowise (~15s), so drive an
    // animated progress panel instead of a bare toast. isGeneratingProductHtml
    // also replaces the card list with that panel and locks the flow (see the
    // guards in onSelectHtmlTemplate/nextStep/toggleHtmlTemplateSection).
    if (isProduct) {
      this.isGeneratingProductHtml = true;
      this.startProgress({
        targetValue: 99,
        // S-curve up to 80% over ~14s, then a crawl that decelerates: 0.25s for the
        // first 1%, each later 1% costing 18% more. The bar stays visibly alive well
        // past the typical ~15s without ever parking at "done".
        duration: 14000,
        easing: 'in-out',
        startValue: 3, // so the bar reads as alive during the slow ease-in
        crawlFrom: 80,
        crawlStep: 250,
        crawlGrowth: 1.18,
      });
    } else {
      this.utils.notify.showInfo('Loading template, please wait...');
    }
    this.svc
      .getPageTheme(
        this.storeId,
        themeId,
        lang,
        isProduct ? this.selectedProduct : null,
        country,
        isProduct ? 0 : 1,
      )
      .subscribe(
        (res: any) => {
          const full = this.mapThemeRowToHtmlTemplate(res?.data);
          const content = (full && full.content) || '';
          if (isProduct) {
            // Runs the bar to 100% then clears isLoadingSelectedTheme itself.
            this.progressCompleted();
            this.isGeneratingProductHtml = false;
            if (res?.data?.ai_generated === false) {
              // ai_generated_error carries the middleware's reason (e.g.
              // "Insufficient Credit" from the classic-editor-parity credit gate).
              this.utils.notify.showWarning(
                res?.data?.ai_generated_error === 'Insufficient Credit'
                  ? 'Insufficient credit to generate product content — the template was loaded as-is.'
                  : 'Product content could not be generated — the template was loaded as-is.',
              );
            } else {
              // Only successful generations are reused; a fallback response
              // stays uncached so re-selecting the template retries Flowise.
              this.svc.cacheGeneratedPageTheme(
                this.storeId,
                themeId,
                lang,
                this.selectedProduct,
                country,
                res,
              );
            }
          } else {
            this.isLoadingSelectedTheme = false;
            // Cache so re-selecting doesn't refetch — but only for the card's own
            // theme. A group's Styles share one card, so caching a member's HTML
            // there would make every later Style switch serve the wrong content.
            if (String(themeId) === String(template.themeId)) {
              template.content = content;
            }
          }
          this.applyHtmlTemplateContent(content);
        },
        () => {
          this.stopProductHtmlProgress();
          this.isLoadingSelectedTheme = false;
          this.utils.notify.showError('Unable to load the selected template.');
        },
      );
  }

  /**
   * Load resolved HTML into the editor and switch to the code-editor view.
   *
   * Always repaints the preview (force), never relying on updateHtmlPreview()'s
   * dedup cache: this path replaces the document wholesale (template load, Style
   * switch), and a stale cache entry silently skipping that render leaves the
   * preview showing the previous style.
   */
  private applyHtmlTemplateContent(content: string) {
    // Stamp the chosen template's id into the content so editing the saved page can
    // re-select this template later (frontend-only; rides along in page_content).
    const stamped = this.injectTemplateMarker(content || '', this.selectedHtmlTemplate);
    this.htmlTemplateSelected = true;
    this.readyToSave = true;
    this.htmlEditorContent = stamped;
    this.hasUnsavedChanges = true;

    // Defer so the editor container (*ngIf) is in the DOM before we init/populate it.
    setTimeout(() => {
      this.reinitHtmlEditorIfNeeded();
      this.setHtmlEditorContent(stamped, true);
      // setHtmlEditorContent's dispatch looks like a user edit to CodeMirror, whose
      // listener queues another preview repaint 500ms later. We just rendered the
      // same markup, so drop that timer — it would only re-flash the iframe.
      clearTimeout(this.htmlPreviewDebounceTimer);
    }, 100);
  }

  /**
   * Replace the editor's content, supporting both CodeMirror and the textarea fallback.
   *
   * @param force repaint the preview even if the dedup cache thinks this markup is
   *              already on screen — set when the caller replaced the whole document.
   */
  private setHtmlEditorContent(content: string, force = false) {
    this.htmlEditorContent = content || '';
    if (this.htmlEditorView) {
      this.htmlEditorView.dispatch({
        changes: {
          from: 0,
          to: this.htmlEditorView.state.doc.length,
          insert: this.htmlEditorContent,
        },
      });
    } else if (this.htmlEditorFallbackEl) {
      this.htmlEditorFallbackEl.value = this.htmlEditorContent;
      this.htmlEditorLineCount = this.htmlEditorContent.split('\n').length;
      this.htmlEditorCharCount = this.htmlEditorContent.length;
    }
    this.updateHtmlPreview(force);
  }

  initHtmlEditor(retryCount = 0) {
    if (this.htmlEditorView) return;

    const container = document.getElementById('html-code-editor');
    if (!container) {
      if (retryCount < 10) {
        setTimeout(() => this.initHtmlEditor(retryCount + 1), 200);
      }
      return;
    }

    try {
      // Use textarea fallback if CodeMirror fails
      this.initHtmlEditorCodeMirror(container);
    } catch (e) {
      console.error('ERROR initHtmlEditor, using textarea fallback', e);
      this.initHtmlEditorFallback(container);
    }
  }

  private updateHtmlEditorStats(state) {
    const doc = state.doc;
    this.htmlEditorLineCount = doc.lines;
    this.htmlEditorCharCount = doc.length;
  }

  private updateHtmlCursorPos(state) {
    const pos = state.selection.main.head;
    const line = state.doc.lineAt(pos);
    this.htmlEditorCursorLine = line.number;
    this.htmlEditorCursorCol = pos - line.from + 1;
  }

  private htmlEditorFallbackEl: HTMLTextAreaElement;

  private initHtmlEditorCodeMirror(container: HTMLElement) {
    const htmlState = EditorState.create({
      doc: this.htmlEditorContent || '',
      extensions: [
        basicSetup,
        html(),
        EditorView.updateListener.of((e) => {
          if (e.docChanged) {
            this.htmlEditorContent = e.state.doc.toString();
            this.hasUnsavedChanges = true;
            this.updateHtmlEditorStats(e.state);
            clearTimeout(this.htmlPreviewDebounceTimer);
            this.htmlPreviewDebounceTimer = setTimeout(() => this.updateHtmlPreview(), 500);
          }
          if (e.selectionSet) {
            this.updateHtmlCursorPos(e.state);
          }
        }),
      ],
    });

    this.htmlEditorView = new EditorView({
      state: htmlState,
      parent: container,
    });

    this.editorIsReady = true;
    this.updateHtmlEditorStats(this.htmlEditorView.state);
  }

  private initHtmlEditorFallback(container: HTMLElement) {
    const textarea = document.createElement('textarea');
    textarea.value = this.htmlEditorContent || '';
    textarea.style.cssText = 'width:100%;height:100%;border:none;outline:none;resize:none;padding:12px;font-family:monospace;font-size:13px;tab-size:2;white-space:pre;overflow:auto;';
    textarea.spellcheck = false;
    textarea.addEventListener('input', () => {
      this.htmlEditorContent = textarea.value;
      this.hasUnsavedChanges = true;
      this.htmlEditorLineCount = textarea.value.split('\n').length;
      this.htmlEditorCharCount = textarea.value.length;
      clearTimeout(this.htmlPreviewDebounceTimer);
      this.htmlPreviewDebounceTimer = setTimeout(() => this.updateHtmlPreview(), 500);
    });
    container.appendChild(textarea);
    this.htmlEditorFallbackEl = textarea;
    this.editorIsReady = true;
    this.htmlEditorLineCount = textarea.value.split('\n').length;
    this.htmlEditorCharCount = textarea.value.length;
  }

  /**
   * Render the current content into the preview iframe(s).
   *
   * Assigning srcdoc reloads the frame, which the user sees as a flash, so the
   * markup last written to each frame is remembered and an identical write is
   * skipped. Loading a template or switching Style legitimately reaches this three
   * times in quick succession — reinitHtmlEditorIfNeeded(), setHtmlEditorContent()
   * and the editor's own debounced change listener — and without this guard each
   * one repainted the preview.
   *
   * @param force re-render even when the content is unchanged (manual Refresh
   *              Preview, where the user expects a visible reload)
   */
  updateHtmlPreview(force = false) {
    const content = this.htmlEditorContent || '';
    const iframe = document.getElementById('html-preview-frame') as HTMLIFrameElement;
    if (iframe) {
      this.attachPreviewClickToCode(iframe);
      if (force || this.lastRenderedPreviewHtml !== content) {
        iframe.srcdoc = content;
        this.lastRenderedPreviewHtml = content;
      }
    }
    // Keep the fullpage preview in step — switching Style while fullscreen would
    // otherwise leave the old style rendered until the user exited and returned.
    if (this.htmlPreviewFullscreen) {
      const fullscreenIframe = document.getElementById(
        'html-fullscreen-preview-frame',
      ) as HTMLIFrameElement;
      if (
        fullscreenIframe &&
        (force || this.lastRenderedFullscreenHtml !== content)
      ) {
        fullscreenIframe.srcdoc = content;
        this.lastRenderedFullscreenHtml = content;
      }
    }
  }

  /**
   * Click-to-code: clicking an element in the preview jumps the code editor to
   * its opening tag. The srcdoc stays byte-identical to the source (no markers
   * injected), so the clicked element is located by tag name + occurrence index
   * and matched against CodeMirror's own HTML syntax tree.
   */
  private attachPreviewClickToCode(iframe: HTMLIFrameElement) {
    if (this.previewClickBoundFrame === iframe) return;
    this.previewClickBoundFrame = iframe;
    const bind = () => {
      const doc = iframe.contentDocument;
      if (!doc) return;
      // Capture phase, so the jump wins over any click handlers in the preview.
      doc.addEventListener('click', (e) => this.onPreviewElementClick(e), true);
    };
    // Every srcdoc assignment replaces the frame's document (and its listeners),
    // so rebind on each load; bind() now covers a frame that already rendered.
    iframe.addEventListener('load', bind);
    bind();
  }

  private onPreviewElementClick(event: MouseEvent) {
    const target = event.target as Element;
    if (!target || !target.tagName || !this.htmlEditorView) return;
    // Links/buttons in the preview must not navigate the frame away.
    event.preventDefault();
    const doc = target.ownerDocument;
    // The exact character clicked, when the click landed on text.
    const caret = this.caretFromPoint(doc, event.clientX, event.clientY);
    // Walk up so a click on a script-generated node (absent from the source)
    // still lands on the nearest ancestor that does exist in it.
    for (let el: Element | null = target; el && el !== doc.documentElement; el = el.parentElement) {
      const sameTag = doc.getElementsByTagName(el.tagName);
      const index = Array.prototype.indexOf.call(sameTag, el);
      const pos = this.findNthTagInSource(el.tagName.toLowerCase(), index);
      if (pos) {
        // Prefer selecting the clicked word; the caret must belong to this very
        // element, or the text lives in a node the source doesn't contain.
        if (caret && caret.node.parentElement === el) {
          const word = this.findClickedWordInSource(caret, pos);
          if (word) {
            this.jumpEditorTo(word.from, word.to);
            return;
          }
        }
        this.jumpEditorTo(pos.from, pos.to);
        return;
      }
    }
  }

  /** caretPositionFromPoint with the older caretRangeFromPoint as fallback. */
  private caretFromPoint(
    doc: Document,
    x: number,
    y: number,
  ): { node: Node; offset: number } | null {
    const d = doc as any;
    if (typeof d.caretPositionFromPoint === 'function') {
      const p = d.caretPositionFromPoint(x, y);
      return p ? { node: p.offsetNode, offset: p.offset } : null;
    }
    if (typeof d.caretRangeFromPoint === 'function') {
      const r = d.caretRangeFromPoint(x, y);
      return r ? { node: r.startContainer, offset: r.startOffset } : null;
    }
    return null;
  }

  /**
   * Source range of the word under the caret, or null when it can't be located
   * (clicked whitespace, or entities/normalization made the DOM text differ from
   * the source) — the caller then falls back to selecting the opening tag.
   */
  private findClickedWordInSource(
    caret: { node: Node; offset: number },
    el: { contentFrom: number; contentTo: number },
  ): { from: number; to: number } | null {
    if (caret.node.nodeType !== Node.TEXT_NODE) return null;
    const text = caret.node.nodeValue || '';
    // Expand around the clicked character to whitespace boundaries.
    let start = Math.min(caret.offset, text.length);
    let end = start;
    while (start > 0 && !/\s/.test(text[start - 1])) start--;
    while (end < text.length && !/\s/.test(text[end])) end++;
    if (start === end) return null;

    const state = this.htmlEditorView.state;
    const content = state.doc.sliceString(el.contentFrom, el.contentTo);
    // DOM text nodes keep source whitespace, so the whole node usually matches
    // byte-for-byte — anchoring the word at its offset within the node.
    const nodeIdx = content.indexOf(text);
    if (nodeIdx >= 0) {
      return { from: el.contentFrom + nodeIdx + start, to: el.contentFrom + nodeIdx + end };
    }
    // Entities (&amp; → &) break the exact match — try the word alone.
    const word = text.slice(start, end);
    const wordIdx = content.indexOf(word);
    if (wordIdx >= 0) {
      return { from: el.contentFrom + wordIdx, to: el.contentFrom + wordIdx + word.length };
    }
    return null;
  }

  /**
   * Position of the Nth (0-based) opening `<tag` in the source, via the syntax
   * tree. from/to select the tag name; contentFrom/contentTo span the element's
   * inner source (empty for self-closing/void elements).
   */
  private findNthTagInSource(
    tag: string,
    n: number,
  ): { from: number; to: number; contentFrom: number; contentTo: number } | null {
    if (n < 0) return null;
    const state = this.htmlEditorView.state;
    let count = 0;
    let result: ReturnType<PageManageComponent['findNthTagInSource']> = null;
    syntaxTree(state).iterate({
      enter: (node) => {
        if (result) return false;
        // TagName also appears under CloseTag and in the nested CSS parse of
        // <style> blocks (type selectors like `h1 {`) — only HTML opening/
        // self-closing tags correspond to elements the preview DOM counts.
        const parentName = node.node.parent?.name;
        if (
          node.name !== 'TagName' ||
          (parentName !== 'OpenTag' && parentName !== 'SelfClosingTag')
        ) return;
        if (state.doc.sliceString(node.from, node.to).toLowerCase() !== tag) return;
        if (count++ === n) {
          const openTag = node.node.parent;
          const element = openTag?.parent;
          const contentFrom = openTag ? openTag.to : node.to;
          let contentTo = contentFrom;
          if (element && element.name === 'Element') {
            const last = element.lastChild;
            contentTo = last && last.name === 'CloseTag' ? last.from : element.to;
          }
          result = {
            from: node.from,
            to: node.to,
            contentFrom,
            contentTo: Math.max(contentFrom, contentTo),
          };
        }
      },
    });
    return result;
  }

  private jumpEditorTo(from: number, to: number) {
    // Preview-only mode: don't force the code pane open — select under the hood
    // and remember the spot, so switching to Code/Split later reveals it.
    if (this.htmlEditorViewMode === 'preview') {
      this.htmlEditorView.dispatch({ selection: { anchor: from, head: to } });
      this.pendingEditorScrollPos = { from, to };
      return;
    }
    this.htmlEditorView.dispatch({
      selection: { anchor: from, head: to },
      // Scroll the whole range (not just its start) and center horizontally,
      // so a selection deep in an overflowing line isn't cut off at the edge.
      effects: EditorView.scrollIntoView(EditorSelection.range(from, to), {
        y: 'center',
        x: 'center',
      }),
    });
    this.htmlEditorView.focus();
  }

  @HostListener('window:keydown', ['$event'])
  handleGlobalShortcuts(event: KeyboardEvent) {
    if ((event.ctrlKey || event.metaKey) && (event.key === 'f' || event.key === 'F')) {
      if (
        this.selectedEditorType === 'html' &&
        this.currentStep === 3 &&
        this.htmlTemplateSelected &&
        this.htmlEditorView
      ) {
        event.preventDefault();
        event.stopPropagation();
        this.openHtmlEditorSearch();
      }
    }
  }

  openHtmlEditorSearch() {
    if (!this.htmlEditorView) return;
    if (this.htmlEditorViewMode === 'preview') {
      this.setHtmlEditorViewMode('split');
    }
    setTimeout(() => {
      if (this.htmlEditorView) {
        this.htmlEditorView.focus();
        openSearchPanel(this.htmlEditorView);
      }
    }, 50);
  }

  toggleHtmlPreviewFullscreen() {
    this.htmlPreviewFullscreen = !this.htmlPreviewFullscreen;
    document.documentElement.style.overflow = this.htmlPreviewFullscreen ? 'hidden' : '';
    if (this.htmlEditorViewMode === 'split' || this.htmlEditorViewMode === 'preview') {
      setTimeout(() => {
        this.updateHtmlPreview(true);
      }, 50);
    }
    setTimeout(() => {
      if (this.htmlEditorView) {
        this.htmlEditorView.requestMeasure();
        this.htmlEditorView.focus();
      }
    }, 100);
  }

  copyHtmlContent() {
    if (!this.htmlEditorContent) return;
    navigator.clipboard.writeText(this.htmlEditorContent).then(() => {
      this.utils.notify.showSuccess('HTML copied to clipboard!');
    });
  }

  formatHtmlContent() {
    if (!this.htmlEditorView || !this.htmlEditorContent) return;
    const formatted = this.simpleHtmlFormat(this.htmlEditorContent);
    this.htmlEditorView.dispatch({
      changes: { from: 0, to: this.htmlEditorView.state.doc.length, insert: formatted },
    });
  }

  private simpleHtmlFormat(html: string): string {
    let formatted = '';
    let indent = 0;
    const tab = '  ';
    const selfClosingTags = ['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr'];

    // Split by tags while keeping tags
    const tokens = html.replace(/>\s*</g, '>\n<').split('\n');

    tokens.forEach((token) => {
      const trimmed = token.trim();
      if (!trimmed) return;

      // Closing tag
      if (/^<\//.test(trimmed)) {
        indent = Math.max(0, indent - 1);
      }

      formatted += tab.repeat(indent) + trimmed + '\n';

      // Opening tag (not self-closing, not closing, not doctype/comment)
      if (/^<[a-zA-Z]/.test(trimmed) && !trimmed.endsWith('/>')) {
        const tagName = trimmed.match(/^<([a-zA-Z][a-zA-Z0-9]*)/)?.[1]?.toLowerCase();
        if (tagName && !selfClosingTags.includes(tagName) && !/^<\//.test(trimmed)) {
          indent++;
        }
      }
    });

    return formatted.trim();
  }

  setHtmlEditorViewMode(mode: 'split' | 'code' | 'preview') {
    this.htmlEditorViewMode = mode;
    if (mode === 'preview' || mode === 'split') {
      this.updateHtmlPreview();
    }
    if (this.htmlEditorView) {
      setTimeout(() => {
        this.htmlEditorView.requestMeasure();
      }, 50);
    }
    // Code pane just became visible — reveal the selection a preview click made
    // while it was hidden. Deferred a tick so CodeMirror can measure the pane.
    if (this.pendingEditorScrollPos !== null) {
      const pos = this.pendingEditorScrollPos;
      this.pendingEditorScrollPos = null;
      setTimeout(() => {
        if (!this.htmlEditorView) return;
        this.htmlEditorView.dispatch({
          effects: EditorView.scrollIntoView(
            EditorSelection.range(pos.from, pos.to),
            { y: 'center', x: 'center' },
          ),
        });
        this.htmlEditorView.focus();
      }, 50);
    }
  }


  clearHtmlEditor() {
    if (this.htmlEditorView) {
      this.htmlEditorView.dispatch({
        changes: { from: 0, to: this.htmlEditorView.state.doc.length, insert: '' },
      });
    } else if (this.htmlEditorFallbackEl) {
      this.htmlEditorFallbackEl.value = '';
      this.htmlEditorContent = '';
      this.htmlEditorLineCount = 0;
      this.htmlEditorCharCount = 0;
      this.updateHtmlPreview();
    }
  }

  reinitHtmlEditorIfNeeded() {
    const container = document.getElementById('html-code-editor');
    if (!container) return;
    // If CodeMirror editor exists and is still attached, do nothing
    if (this.htmlEditorView && container.contains(this.htmlEditorView.dom)) {
      return;
    }
    // If fallback textarea exists and is still attached, update its value
    if (this.htmlEditorFallbackEl && container.contains(this.htmlEditorFallbackEl)) {
      this.htmlEditorFallbackEl.value = this.htmlEditorContent || '';
      return;
    }
    // Destroy stale reference and re-create with existing content
    if (this.htmlEditorView) {
      this.htmlEditorView.destroy();
      this.htmlEditorView = null;
    }
    this.initHtmlEditor();
    this.updateHtmlPreview();
    console.log("updateHMTLPreview");
  }

  destroyHtmlEditor() {
    if (this.htmlEditorView) {
      this.htmlEditorView.destroy();
      this.htmlEditorView = null;
    }
  }

  private buildIframeUrl(
    countryCode: string,
    languageCode: string,
    pageUrl: string,
    storeId: string,
  ) {
    let iframeUrl = `${environment.editorBaseUrl}/editor?path=${countryCode}/${languageCode}/product/${pageUrl}&store_id=${storeId}&is_lp=true`;
    const platform = environment.partnerPlatform;
    console.log('platform', platform);
    if (platform === 'estoreglobe' || platform === 'globemerce') {
      iframeUrl += `&platform=${platform}`;
    }
    this.currentIframeUrl = iframeUrl;
    // Reset cached URL so it gets regenerated with the new URL
    this.cachedSafeUrl = null;
    // The iframe reloads with it: hold inits until the new editor says 'ready'
    this.isIframeAppReady = false;
    this.iframeInitTarget = null;
  }

  private callCreateNewCustomPageAndGotoDesign() {
    this.customPageIsLoading = true;
    this.startProgress();
    const params = {
      store_id: this.storeId,
      country_code: this.filteredCountry,
      language_code: this.language_code,
      disable_ai_generate: +this.generateMethod === 1 ? 1 : 0,
      content_product_name: this.selectedProduct,
      product_series: this.selectedSeries,
      page_url: this.customPageData.page_url,
      page_title: this.customPageData.page_title,
    };
    this.svc.createNewCustomPage(this.bearer, params).subscribe(
      (res: any) => {
        this.getNewApi = res.data;
        if (res.data.ref) {
          this.customPageData.page_url = res.data.ref;
        }
        this.selectedTheme = res.data.theme_id;
        this.buildIframeUrl(
          this.filteredCountry,
          this.language_code,
          res.data.ref || this.customPageData.page_url,
          this.storeId,
        );
        this.progressCompleted();
        setTimeout(() => {
          this.customPageIsLoading = false;
          this.readyToSave = true;
        }, 1500);
      },
      (error) => {
        this.utils.notify.showError(error.error.message, 'Error!');
        this.customPageIsLoading = false;
        this.progressValue = 0;
        this.router.navigate(['/dashboard/pages']);
      },
    );
  }

  getNewEditorUrl() {
    // Cache the sanitized URL to prevent iframe from reloading repeatedly
    if (!this.cachedSafeUrl && this.currentIframeUrl) {
      this.cachedSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        this.currentIframeUrl,
      );
    }
    return this.cachedSafeUrl;
  }

  /**
   * Post a message to the iframe
   * @param message - The message data to send
   */
  /** Returns the window the message was posted to, or null if it was not. */
  private postMessageToIframe(message: any): Window | null {
    const iframe = this.newEditorIframe?.nativeElement;

    if (!iframe?.contentWindow) {
      return null;
    }

    try {
      iframe.contentWindow.postMessage(message, this.IFRAME_ORIGIN);
      return iframe.contentWindow;
    } catch (error) {
      console.error('Failed to post message to iframe:', error);
      return null;
    }
  }

  /**
   * Setup listener for messages from the iframe
   */
  private setupIframeMessageListener(): void {
    // Remove existing listener if it exists to prevent duplicates
    if (this.iframeMessageHandler) {
      window.removeEventListener('message', this.iframeMessageHandler);
    }

    this.iframeMessageHandler = (event: MessageEvent) => {
      if (event.origin !== this.IFRAME_ORIGIN) {
        return;
      }

      const { app, action, payload } = event.data || {};

      if (app !== 'eStore') {
        return;
      }

      switch (action) {
        case 'ready': {
          // The editor says 'ready' when its page has mounted, and again after
          // each init it applies. From the window an init was just posted to,
          // it answers that init; any other 'ready' is a freshly loaded editor
          // (first open, a new iframe src, a re-created iframe), which gets the
          // current init. This does not wait for the iframe's load event: that
          // fires only once every image and frame in the editor has loaded,
          // long after 'ready' on a converted classic page.
          const answersInit =
            !!this.iframeInitTarget && event.source === this.iframeInitTarget;
          this.iframeInitTarget = null;
          this.isIframeAppReady = true;
          this.editorIsReady = true;
          this.customPageIsSaving = false;
          this.editPageIsloading = false;
          const init =
            this.pendingIframeInitMessage ??
            (answersInit ? null : this.buildStudioInitMessage());
          this.pendingIframeInitMessage = null;
          if (init) {
            this.sendInitToIframe(init);
          }
          break;
        }

        case 'openImageUploader':
          this.openIframeImageUploader(payload);
          break;

        case 'saved':
          // A second click while the sections are still being stored: the
          // save under way already reloads the editor once it is done
          if (this.studioSectionsSaving) {
            this.studioContinueToSettings = false;
            break;
          }
          this.studioSavedPayload = payload;
          this.customPageIsSaving = true;

          if (this.isClassicUpgrade) {
            if (this.studioContinueToSettings) {
              this.studioContinueToSettings = false;
              this.pendingUpgradePayload = payload;
              this.customPageIsSaving = false;
              this.gotoStep(4);
            } else {
              this.saveClassicUpgrade(payload);
            }
            break;
          }

          if (this.studioContinueToSettings) {
            // Continue: call create_from_json only, then go to Settings
            this.studioContinueToSettings = false;
            this.saveStudioSections(payload).subscribe(
              (res: any) => {
                this.customPageIsSaving = false;
                this.gotoStep(4);
              },
              (error) => {
                this.customPageIsSaving = false;
                this.utils.notify.showError(error.error.message, 'Error!');
              },
            );
          } else {
            // Save: create_from_json, then page/create or page/update, which
            // reloads the editor. One after the other: page/update used to
            // finish first, so the editor reloaded with the old sections
            // while the new ones were still being written, and a save from
            // there overlapped the one under way.
            const behaviour = this.isEditPage
              ? SAVE_BEHAVIOUR.SAVE_AND_EDIT
              : SAVE_BEHAVIOUR.SAVE_AND_CREATE_NEW;
            this.saveStudioSections(payload).subscribe(
              (res: any) => {
                this.showLeaveConfirmation = false;
                this.hasUnsavedChanges = false;
                this.callStoreCustomPage(null, behaviour);
              },
              (error) => {
                this.customPageIsSaving = false;
                this.utils.notify.showError(error.error.message, 'Error!');
              },
            );
          }
          break;
      }
    };

    window.addEventListener('message', this.iframeMessageHandler);
  }

  /**
   * Open the partners-dashboard image upload modal on behalf of the studio
   * editor iframe, then post the resulting URL back to the iframe.
   */
  private openIframeImageUploader(payload: any): void {
    const requestId = payload?.requestId;
    if (!requestId) {
      return;
    }

    const modalRef = this.modalService.open(UploadImageComponent, {
      size: 'lg',
      backdrop: 'static',
    });

    modalRef.result
      .then((result) => {
        const url = Array.isArray(result) ? result[0] : null;
        const mediaId = Array.isArray(result) ? result[1] ?? null : null;
        if (!url) {
          this.postMessageToIframe({
            app: 'eStore',
            action: 'imageUploaded',
            payload: { requestId, url: null, mediaId: null },
          });
          return;
        }
        this.postMessageToIframe({
          app: 'eStore',
          action: 'imageUploaded',
          payload: { requestId, url, mediaId },
        });
      })
      .catch(() => {
        this.postMessageToIframe({
          app: 'eStore',
          action: 'imageUploaded',
          payload: { requestId, url: null, mediaId: null },
        });
      });
  }

  /**
   * Watch for theme changes on <html> element and notify the iframe
   */
  private setupThemeObserver(): void {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }

    this.themeObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class'
        ) {
          const theme = this.utils.getCurrentColorScheme();
          this.postMessageToIframe({
            app: 'eStore',
            action: 'themeChange',
            payload: { theme },
          });
        }
      }
    });

    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  /**
   * Iframe load event. The editor's own 'ready' usually comes first and has
   * already been answered with an init (see the 'ready' handler), so this only
   * keeps the queued init current while the editor has not said 'ready' yet.
   * It must not reset the handshake: an init queued after 'ready' is never sent.
   */
  onIframeLoad(): void {
    if (!this.isIframeAppReady) {
      this.pendingIframeInitMessage = this.buildStudioInitMessage();
    }
  }

  /** Post an init and remember the window that should answer it with 'ready'. */
  private sendInitToIframe(message: any): void {
    this.iframeInitTarget = this.postMessageToIframe(message);
  }

  /**
   * Build the 'init' message the Studio iframe expects. Shared by the iframe's own
   * load handler and by applyStudioTemplateToEditor(), so a template swap seeds the
   * editor through exactly the same contract as a fresh load.
   */
  private buildStudioInitMessage() {
    const payload: any = {
      country_code: this.filteredCountry,
      language_code: this.language_code,
      location_type: 'landing_page',
      category: 'product',
      ref: this.customPageData.page_url,
      template_name: this.customPageData.page_title,
      theme: this.utils.getCurrentColorScheme(),
      store_id: this.storeId,
    };

    const platform = environment.partnerPlatform;
    if (platform === 'estoreglobe' || platform === 'globemerce') {
      payload.platform = platform;
    }

    // A selected Studio starter template (e.g. a migrated "Affiliate Buddy" template)
    // wins: seed the editor with its section array. This is what makes a template swap
    // in edit mode take effect, as well as the initial pick in the create flow.
    // Otherwise page_content from page/info is authoritative — pass it through so the
    // iframe doesn't render stale data from template/site/{category}/{ref}. That branch
    // is also what the Product/Blank cards fall into during edit: they carry no section
    // array, so the page keeps the content it already has rather than being wiped.
    if (
      this.selectedEditorType === 'new' &&
      Array.isArray(this.selectedStudioTemplateData)
    ) {
      payload.template_data = this.selectedStudioTemplateData;
    } else if (Array.isArray(this.customPageData?.page_content)) {
      payload.template_data = this.customPageData.page_content;
    }

    return {
      app: 'eStore',
      action: 'init',
      payload,
    };
  }

  /**
   * Edit mode: apply the template just picked on the "Select a Template" subpage to the
   * editor that is already open, without reloading the page or touching page_url.
   *
   * The create flow gets away with calling get_new here because it has no page yet. In
   * edit mode that call is both wrong and insufficient: it rewrites page_url from
   * res.data.ref, silently repointing the wizard at a different page, and the template
   * only ever reached the editor as a side effect of buildIframeUrl() changing the
   * iframe src — the (load) event is what runs onIframeLoad() and queues the init
   * message. When the ref came back unchanged the src never changed, so no load fired
   * and Continue appeared to do nothing at all.
   *
   * Post the init message straight to the running editor instead, or queue it if the
   * iframe app has not handshaked yet (same contract as onIframeLoad).
   */
  private applyStudioTemplateToEditor(): void {
    const message = this.buildStudioInitMessage();

    if (this.isIframeAppReady) {
      this.sendInitToIframe(message);
    } else {
      this.pendingIframeInitMessage = message;
    }

    // Swapping the content is an unsaved edit, and Save must stay reachable.
    this.readyToSave = true;
    this.hasUnsavedChanges = true;
  }

  productNamesInSeries;

  generateProductNamesInSeries(seriesName = null) {
    seriesName ||= this.selectedSeries;
    if (!seriesName) {
      return;
    }

    const productInSeries = this.productNameBySeries[seriesName];
    const newProductInSeries = [];
    if (productInSeries) {
      Object.keys(productInSeries).map((productName) => {
        const productInfo = productInSeries[productName];
        newProductInSeries.push({
          name: productName,
          image: productInfo?.image,
          product_content_key: productInfo?.product_content_key,
        });
      });
    }
    this.productNamesInSeries = newProductInSeries;
  }

  // Initialised (not bare `progressValue;`) because the animation compares it
  // numerically: `undefined < rampTarget` is false, which used to send the very
  // first run straight past the ramp into the crawl.
  progressValue = 0;

  animationFrameId: number | null = null;

  /**
   * Drive progressValue from 0 toward targetValue over `duration`.
   *
   * options.easing picks the curve:
   *  - 'out' (default) — quick off the mark, then a long decelerating tail.
   *  - 'in-out' — S-curve: eases in slowly, accelerates through the middle,
   *    settles slowly. Reads as "warming up → working → finishing", which suits
   *    a wait whose real duration is roughly known (product AI generation).
   *
   * options.crawlFrom (with 'in-out') switches the bar to a decelerating crawl at
   * that percentage: the S-curve covers startValue → crawlFrom over `duration`,
   * then each further 1% costs crawlStep ms times crawlGrowth per step, so the bar
   * keeps inching without ever implying it has finished. It never reaches
   * targetValue on its own — progressCompleted() snaps it to 100% when the request
   * lands, which is the point: an overrunning request still looks alive.
   */
  startProgress(options: any = {}) {
    const targetValue = options.targetValue ?? 80;
    const duration = options.duration ?? 20000;
    // Floor the bar so a slow ease-in doesn't look stalled at 0%.
    const startValue = options.startValue ?? 0;
    // Percentage at which the S-curve hands over to the crawl (null = no crawl).
    const crawlFrom = options.crawlFrom ?? null;
    // ms for the first 1% of the crawl, then multiplied by crawlGrowth each step.
    const crawlStep = options.crawlStep ?? 250;
    const crawlGrowth = options.crawlGrowth ?? 1.18;

    // Cancel any in-flight animation and start from a known value. Without this the
    // ramp's `progressValue < rampTarget` guard reads leftover state — undefined on
    // the first ever run, or a stale percentage if a previous run was interrupted —
    // and skips straight to the crawl.
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.progressValue = startValue;

    let start = null;
    const easeOutQuart = (t: number) => 1 - Math.pow(1 - t, 6); // Adjusted easing function
    // Symmetric quartic S-curve: slow, fast, slow.
    const easeInOutQuart = (t: number) =>
      t < 0.5 ? 8 * Math.pow(t, 4) : 1 - Math.pow(-2 * t + 2, 4) / 2;
    const ease = options.easing === 'in-out' ? easeInOutQuart : easeOutQuart;

    // The eased ramp ends here; anything above is the crawl's job.
    const rampTarget =
      crawlFrom !== null ? Math.min(crawlFrom, targetValue) : targetValue;

    // Crawl bookkeeping: when the next 1% is due, and what it costs. nextStepAt is
    // seeded off the moment the ramp actually lands on rampTarget rather than off
    // `duration` — rounding gets there slightly early, and using `duration` would
    // stall the bar on the threshold for the difference.
    let crawlValue = rampTarget;
    let nextStepAt: number | null = null;
    let stepCost = crawlStep;

    const animate = (timestamp: number) => {
      if (!start) {
        start = timestamp;
      }
      const elapsedTime = timestamp - start;

      const onRamp =
        crawlFrom === null ||
        (elapsedTime < duration && this.progressValue < rampTarget);
      if (onRamp) {
        const normalizedTime = elapsedTime / duration;
        const easedTime = ease(Math.min(normalizedTime, 1));
        this.progressValue = Math.round(
          startValue + easedTime * (rampTarget - startValue),
        );
      } else {
        if (nextStepAt === null) {
          nextStepAt = elapsedTime + crawlStep;
          // Continue from whatever the ramp actually reached, so handover is seamless
          // even if rounding left it a point short of rampTarget.
          crawlValue = Math.max(crawlValue, this.progressValue ?? rampTarget);
        }
        // Past the ramp: advance 1% each time its (growing) interval elapses.
        while (elapsedTime >= nextStepAt && crawlValue < targetValue) {
          crawlValue += 1;
          stepCost *= crawlGrowth;
          nextStepAt += stepCost;
        }
        this.progressValue = crawlValue;
      }

      if (this.progressValue < targetValue) {
        this.animationFrameId = requestAnimationFrame(animate); // Store the animation frame ID
      }
    };

    this.animationFrameId = requestAnimationFrame(animate); // Start the animation and store the animation frame ID
  }

  progressCompleted() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId); // Stop the previous animation if it's running
    }

    const targetValue = 100;
    const duration = 1000;

    let start = null;
    const currentProgress = this.progressValue; // Store the current progress value

    const easeOutQuart = (t: number) => 1 - Math.pow(1 - t, 4); // Easing function

    const animate = (timestamp: number) => {
      if (!start) {
        start = timestamp;
      }
      const elapsedTime = timestamp - start;
      const normalizedTime = elapsedTime / duration;
      const easedTime = easeOutQuart(Math.min(normalizedTime, 1));
      this.progressValue = Math.round(
        currentProgress + easedTime * (targetValue - currentProgress),
      ); // Start from current progressValue

      if (this.progressValue < targetValue) {
        this.animationFrameId = requestAnimationFrame(animate);
      } else {
        setTimeout(() => {
          this.isLoadingSelectedTheme = false;
          this.progressValue = 0;
        }, 500);
      }
    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  private showApiError(error: any) {
    let errorMsg = error?.error?.messages?.error ?? error?.message;
    const apiMessage = error?.error?.message;
    // fails(['error' => '...']) sends a plain string, and Object.values would
    // split it into one character per line; validation errors send an object.
    const errorMsgArr =
      typeof apiMessage === 'string'
        ? [apiMessage].filter(Boolean)
        : Object.values(apiMessage ?? []);

    if (error?.error?.status === 'error' && errorMsgArr?.length) {
      errorMsg = (errorMsgArr as string[]).join('\n');
    }
    this.utils.msgBox.open({
      message: this.utils.translate(errorMsg),
      title: this.utils.translate('Error'),
      icon: 'error',
      contentClassList: 'whitespace-pre-line',
    });
  }

  get stepPercentage() {
    const currentStep = this.currentStep;

    // The home page flow hides two of the five steps, so counting raw indices
    // would jump the bar from 20% to 80% across a single Continue.
    if (this.isHomePage) {
      const visibleSteps = this.stepSchema.filter((step) =>
        this.isStepVisible(step),
      );
      const visibleIndex = visibleSteps.indexOf(this.stepSchema[currentStep]);
      if (visibleIndex !== -1) {
        return Math.ceil(((visibleIndex + 1) / visibleSteps.length) * 100);
      }
    }

    if (this.selectedEditorType === 'new') {
      const designPageIndex = this.stepSchema.findIndex(
        (step) => step.name === 'Design page',
      );
      const visibleMaxSteps = designPageIndex + 1;
      return Math.ceil(((currentStep + 1) / visibleMaxSteps) * 100);
    }

    const maxSteps = this.stepSchema.length;
    return Math.ceil(((currentStep + 1) / maxSteps) * 100);
  }

  get isFinalStep() {
    const maxSteps = this.stepSchema.length,
      currentStep = this.currentStep;

    if (this.selectedEditorType === 'new') {
      const settingsIndex = this.stepSchema.findIndex(
        (step) => step.name === 'Settings',
      );
      return +currentStep >= +settingsIndex;
    }

    if (this.selectedEditorType === 'html') {
      // On the Design page step the picker is shown first (Continue -> editor);
      // only the code editor itself is the final, saveable step.
      const designPageIndex = this.stepSchema.findIndex(
        (step) => step.name === 'Design page',
      );
      return this.htmlTemplateSelected && +currentStep >= +designPageIndex;
    }

    return +currentStep >= +maxSteps - 1;
  }

  get isFirstStep() {
    return +this.currentStep <= 0;
  }

  markAsChanged() {
    if (this.isEditPage) {
      this.hasUnsavedChanges = true;
    }
  }

  async showPageThemeSelector(countryCode) {
    return new Promise((resolve, reject) => {
      this.svc.getPageThemeList(countryCode).subscribe((res: any) => {
        if (!!res?.data?.length) {
          // The classic theme list, now read only by getDefaultTemplate() for
          // the selectedTheme a save sends as page_theme. Keeps out:
          //  • HTML-editor templates (theme_categories_id includes 5) — served by
          //    the separate HTML picker. Category 5 is the single marker for these
          //    (including product HTML templates); `type` is not a discriminator.
          //  • Malformed placeholder rows with a blank `type` (leftover empty
          //    HTML-template inserts: null category, no content) — never valid
          //    classic themes; every real theme has a non-empty type.
          this.pageThemeList = res.data.filter(
            (theme: any) =>
              !this.isHtmlEditorTheme(theme) &&
              String(theme?.type ?? '').trim() !== '' &&
              String(theme?.type ?? '').trim() !== 'product',
          );
        }
        setTimeout(() => {
          resolve(true);
        }, 1);
      });
    });
  }

  toggleFullPageMode() {
    this.fullPageMode = !this.fullPageMode;
  }

  getPageType(type: string) {
    return type === 'custom' ? 1 : 0;
  }

  getCustomPage(pageId: number) {
    this.customPageIsLoading = true;
    this.editPageIsloading = true;

    const params = {
      store_page_id: pageId,
      store_id: this.storeId,
    };

    this.svc.getCustomPage(params, this.bearer).subscribe(
      (res: any) => {
        this.currentEditPageUrl = res.data.page_url;
        this.ogimgURL =
          res.data.metadata?.og_image ?? this.customPageData.metadata?.og_image;

        let pageContent = res.data.page_content;
        if (typeof pageContent === 'string') {
          try {
            pageContent = JSON.parse(pageContent);
          } catch {
            // Not JSON — old HTML-editor pages saved the raw markup directly.
            // Keep the string; the html branch below loads it into the editor.
          }
        }
        pageContent = pageContent ?? this.customPageData.page_content;
        let pageOption =
          (typeof res.data.page_option === 'string'
            ? JSON.parse(res.data.page_option)
            : res.data.page_option) ?? this.customPageData.page_option;

        // Get selectedParameters from
        let selectedParams = pageContent?.selFectedParams;
        let selectedParamsObj: any = {};

        if (selectedParams === '' || selectedParams === undefined) {
          selectedParamsObj = {
            selectedTheme: -1,
            language_code: 'en-US',
            generateMethod: 1,
            disableAiGenerate: 0,
          };
        } else {
          try {
            selectedParamsObj = JSON.parse(selectedParams);
          } catch {}
        }

        // Reopening the store's home page: carry is_home through, so saving does
        // not hand the home route back to the built-in storefront home page.
        this.isHomePage = !!+(res.data?.is_home ?? 0);
        this.selectedTheme =
          res.data?.page_theme ?? selectedParamsObj?.selectedTheme;
        this.selectedProduct =
          res.data?.product_name ?? selectedParamsObj?.selectedProduct;
        this.selectedSeries =
          res.data?.product_series ?? selectedParamsObj?.selectedSeries;
        this.filteredCountry =
          res.data?.country_code ??
          selectedParamsObj?.country_code ??
          this.filteredCountry;
        this.language_code =
          res.data?.language_code ?? selectedParamsObj?.language_code;
        this.generateMethod =
          this.getPageType(res.data?.type) ?? selectedParamsObj?.generateMethod;
        const selectedLanguage = this.languages.find(
          (lang) => lang.language_code === this.language_code,
        );
        this.language_name = selectedLanguage?.language_name;
        const selectedAB = res?.data?.affiliate_list ?? [];

        if (selectedAB.length > 0) {
          this.selectedABOptions = selectedAB
            .filter((buddy) => +buddy.status === 1)
            .map((buddy) => buddy.dealer_code);
        } else {
          this.selectedABOptions = selectedAB;
        }

        this.changeCountry(this.filteredCountry, false);

        this.customPageData = {
          store_id: this.storeId,
          store_page_id: res.data.store_page_id,
          page_url: res.data.page_url ?? this.customPageData.page_url,
          page_title: res.data.page_title ?? this.customPageData.page_title,
          page_content: pageContent,
          page_option: pageOption,
          visibility: +res.data.visibility ?? this.customPageData.visibility,
          page_theme: +res.data.page_theme ?? this.customPageData.page_theme,
          metadata: {
            page_title:
              res.data.metadata?.page_title ??
              this.customPageData.metadata?.page_title,
            keywords:
              res.data.metadata?.keywords ??
              this.customPageData.metadata?.keywords,
            description:
              res.data.metadata?.description ??
              this.customPageData.metadata?.description,
            og_image:
              res.data.metadata?.og_image ??
              this.customPageData.metadata?.og_image,
          },
        };

        // A full-width classic page converts to full-width Studio sections
        // (openClassicPageInStudio reads this same page_option object).
        if (pageContent.additionalConfig?.fixedWidth === false) {
          this.customPageData.page_option.container_style = 2;
        }

        if (
          res.data.editor === 'html' ||
          pageContent?.type === 'html' ||
          typeof pageContent === 'string'
        ) {
          this.selectedEditorType = 'html';
          // Editing an existing page goes straight to the code editor — skip the
          // template picker since the page already has content.
          this.htmlTemplateSelected = true;
          this.htmlEditorContent = typeof pageContent === 'string'
            ? pageContent
            : pageContent?.content || '';
          this.readyToSave = true;
          this.customPageIsLoading = false;
          this.editPageIsloading = false;
          this.gotoStep(3);
          setTimeout(() => {
            this.initHtmlEditor();
            this.updateHtmlPreview();
            this.hasUnsavedChanges = false;
          }, 150);
        } else if (res.data.page_theme_type === 'new' || res.data.editor === 'studio') {
          // Load custom JS from metadata.js (where studio editor saves it)
          if (res.data.metadata?.js) {
            this.customPageData.page_content.js = res.data.metadata.js;
          }
          this.lostStudioTheme =
            res.data.editor === 'studio' && res.data.page_theme_type !== 'new';
          this.openStudioEditor();
        } else {
          this.openClassicPageInStudio(pageContent, pageOption);
        }
      },
      (error) => {
        this.showApiError(error);
        this.customPageIsLoading = false;
        this.editPageIsloading = false;
      },
    );
  }

  /**
   * Edit mode: open the page's sections (customPageData.page_content) in the
   * Studio editor, straight on its content tab.
   */
  private openStudioEditor() {
    this.selectedEditorType = 'new';
    this.studioTemplateSelected = true;
    this.readyToSave = true;
    // Edit mode never runs onSelectEditor('new') — the Select Editor step is
    // hidden and click-disabled for Studio pages — so preload the starter
    // templates here, or the "Select a Template" subpage (still reachable from
    // the Design page side-nav) renders only the hard-coded Product/Blank cards
    // and the Theme filter stays hidden. Idempotent, same as the HTML preload.
    this.loadStudioTemplates();
    this.buildIframeUrl(
      this.filteredCountry,
      this.language_code,
      this.customPageData.page_url,
      this.storeId,
    );
    this.currentStep = 3;
    this.subpage = 1;
    this.isIframeAppReady = false;
    this.pendingIframeInitMessage = this.buildStudioInitMessage();

    this.customPageIsLoading = false;
    this.editPageIsloading = false;
    this.hasUnsavedChanges = true;
  }

  /**
   * Edit mode, page made in the retired Classic editor: convert its Unlayer
   * design into Studio sections and open those in Studio. Nothing is written
   * here — the live page stays classic until the dealer saves (see
   * saveClassicUpgrade).
   */
  private openClassicPageInStudio(pageContent: any, pageOption: any) {
    let conversion;
    try {
      conversion = convertClassicPage(pageContent, {
        fullWidth: +pageOption?.container_style === 2,
      });
    } catch (error) {
      console.error('Classic page conversion failed', error);
      this.customPageIsLoading = false;
      this.editPageIsloading = false;
      this.utils.notify.showError(
        this.utils.translate(
          'This page could not be opened in the Studio editor. Please contact support.',
        ),
      );
      this.router.navigate(['/dashboard/pages']);
      return;
    }

    const sections: any = conversion.sections;
    // Studio keeps a page's custom JS in metadata.js; callStoreCustomPage moves
    // it there from this property, as for any Studio page.
    if (conversion.js) {
      sections.js = conversion.js;
    }
    this.customPageData.page_content = sections;
    this.isClassicUpgrade = true;
    this.classicUpgradeReport = conversion.report;
    this.pendingUpgradePayload = null;
    this.classicPageTheme = +this.selectedTheme || null;
    this.selectedStudioTemplateData = null;
    this.showPreviewButton = false;

    this.openStudioEditor();
  }

  /**
   * First save of a converted classic page, saved the way any Studio page is:
   * create_from_json stores the sections, then page/update (callStoreCustomPage)
   * switches the page to the Studio base theme and editor 'studio'. One after
   * the other, because a Studio page whose sections are not there yet is served
   * a default template. page/update leaves page_content alone (it is sent as
   * null), so the classic design stays in that column as the backup, and
   * page_option.classic_page_theme keeps the classic theme to restore it with.
   */
  private saveClassicUpgrade(payload: any) {
    // One save at a time (see saveStudioSections), the base theme lookup
    // included
    if (this.studioSectionsSaving) {
      return;
    }
    this.studioSectionsSaving = true;
    this.customPageIsSaving = true;
    const { theme, ...sectionsPayload } = payload ?? {};

    this.withStudioBaseTheme(
      () => {
        this.saveStudioSections({
          ...sectionsPayload,
          ref: this.customPageData.page_url,
        }).subscribe(
          () => this.callStoreCustomPage(null, SAVE_BEHAVIOUR.SAVE_AND_EDIT),
          (error) => {
            this.customPageIsSaving = false;
            this.showApiError(error);
          },
        );
      },
      () => (this.studioSectionsSaving = false),
    );
  }

  /**
   * Store the editor's sections (create_from_json), one save at a time.
   * create_from_json replaces a page's sections by deleting the current set
   * and inserting the new one, so two calls that overlapped each inserted a
   * full set and the page showed every section twice. A second call was easy
   * to make: the editor's own Save Changes button unlocks after 1.5s whatever
   * the save is doing, and the header Save locks only once 'saved' arrives.
   * While this runs, the 'saved' handler drops the editor's answers.
   */
  private saveStudioSections(payload: any): Observable<any> {
    this.studioSectionsSaving = true;
    let answered = false;
    return this.svc.saveNewCustomPage(this.bearer, payload).pipe(
      tap({
        next: () => (answered = true),
        error: () => (answered = true),
      }),
      finalize(() => {
        this.studioSectionsSaving = false;
        // No answer at all: the request timed out, which the HTTP
        // interceptor swallows. Let the dealer save again.
        if (!answered) {
          this.customPageIsSaving = false;
        }
      }),
    );
  }

  /** Run `next` once the Studio base theme is known, fetching it if need be. */
  private withStudioBaseTheme(next: () => void, onFailed?: () => void) {
    if (this.studioBaseThemeId) {
      next();
      return;
    }

    const failed = () => {
      onFailed?.();
      this.customPageIsSaving = false;
      this.utils.notify.showError(
        this.utils.translate(
          'Could not switch this page to the Studio editor. Please try again.',
        ),
      );
    };

    this.svc.getStudioPageThemeList(this.filteredCountry || '').subscribe(
      (res: any) => {
        this.studioBaseThemeId = this.pickStudioBaseTheme(
          Array.isArray(res?.data) ? res.data : [],
        );
        if (this.studioBaseThemeId) {
          next();
        } else {
          failed();
        }
      },
      () => failed(),
    );
  }

  updateCustomPage(e, saveBehaviour = SAVE_BEHAVIOUR.SAVE) {
    if (this.selectedEditorType === 'new') {
      // A converted classic page saved from Settings: the sections came with
      // Continue (or, if Settings was reached another way, as converted)
      if (this.isClassicUpgrade && this.currentStep === 4) {
        this.saveClassicUpgrade(
          this.pendingUpgradePayload ?? this.buildStudioInitMessage().payload,
        );
        return;
      }
      // On Settings step, call page/create or update only (create_from_json already called on continue)
      if (this.currentStep === 4) {
        this.customPageIsSaving = true;
        this.callStoreCustomPage(e, SAVE_BEHAVIOUR.SAVE);
        return;
      }
      // On Content step, post to iframe -> triggers saved handler -> calls create_from_json + page/create
      const message = {
        app: 'eStore',
        action: 'saveBtnCall',
      };
      this.postMessageToIframe(message);
      return;
    }
    if (this.selectedEditorType === 'html') {
      this.saveHtmlPage();
      return;
    }
  }

  saveHtmlPage() {
    // Block saving pages that contain inline base64 images. Embedded images bloat
    // page_content (one page reached ~56MB), slow the editor, and push the
    // page/list endpoint past the backend memory limit (500). Images must be added
    // via the image tool so they upload to the CDN and are stored as URLs.
    const base64ImageCount = (
      this.htmlEditorContent?.match(/data:image\/[a-z0-9.+-]+;base64,/gi) || []
    ).length;
    if (base64ImageCount > 0) {
      this.utils.notify.showError(
        base64ImageCount +
          ' ' +
          this.utils.translate(
            'inline base64 image(s) found. Please re-insert them with the image tool so they upload to the CDN. The page cannot be saved while images are embedded in the HTML.'
          )
      );
      return;
    }

    this.customPageIsSaving = true;

    const body: any = {
      store_id: this.storeId,
      country_code: this.filteredCountry,
      language_code: this.language_code,
      // A new home page has no slug to send — the backend files it under a free
      // one. Editing keeps sending the page's own so the row is found as before.
      page_url:
        this.isHomePage && this.isCreatePage
          ? ''
          : this.customPageData.page_url,
      page_title: this.customPageData.page_title,
      page_content: this.htmlEditorContent,
      page_theme_type: 'html',
      // Claims the storefront's home route for this page, and releases it from
      // whichever page held it before. See Page::saveHtml.
      is_home: this.isHomePage,
    };

    // Persist the chosen HTML starter template's store_page_theme_id as page_theme so
    // edit mode (getInfoPage) can resolve its theme_name. On edit the picker is skipped,
    // so fall back to selectedTheme (restored from the page's saved page_theme); -1 means
    // blank / no template.
    const chosenTemplate = this.htmlTemplates?.find(
      (t) => t.id === this.selectedHtmlTemplate,
    );
    body.page_theme = chosenTemplate?.themeId ?? this.selectedTheme ?? -1;

    if (this.isEditPage) {
      body.store_page_id = this.customPageData.store_page_id;
    }

    this.svc.saveHtmlPage(body, this.bearer).subscribe(
      (res: any) => {
        this.customPageIsSaving = false;
        this.hasUnsavedChanges = false;
        this.showLeaveConfirmation = false;
        const successMsg = this.isCreatePage
          ? 'New HTML page has been saved!'
          : 'HTML page has been updated!';
        this.utils.notify.showSuccess(successMsg);

        this.router.navigateByUrl(this.exitRoute);
      },
      (error) => {
        this.customPageIsSaving = false;
        this.showApiError(error);
      },
    );
  }

  // Studio pages only: HTML pages save through saveHtmlPage().
  callStoreCustomPage(e, saveBehaviour) {
    this.customPageIsSaving = true;

    // The base theme comes with the Studio template list, which may still be
    // on its way; a lost theme must not be saved as -1 again meanwhile
    if (this.lostStudioTheme && !this.studioBaseThemeId) {
      this.withStudioBaseTheme(() => this.callStoreCustomPage(e, saveBehaviour));
      return;
    }

    let body = JSON.parse(JSON.stringify(this.customPageData));

    body['page_content'] = this.isEditPage ? null : this.getNewApi;
    if (this.customPageData.page_content?.js) {
      body['metadata'] = {
        ...(body['metadata'] || {}),
        js: this.customPageData.page_content.js,
      };
    }
    body['product_name'] = this.selectedProduct;
    body['product_series'] = this.selectedSeries;
    body['country_code'] = this.filteredCountry;
    body['language_code'] = this.language_code;
    body['page_theme'] = this.selectedTheme;
    body['type'] = +this.generateMethod === 1 ? 'custom' : 'auto';
    body['affiliate_buddy'] = this.selectedABOptions;

    // First save of a converted classic page (after its sections were saved,
    // see saveClassicUpgrade): this is what makes it a Studio page
    const switchesToStudio = this.isClassicUpgrade && !!this.studioBaseThemeId;
    const restoresStudioTheme = this.lostStudioTheme && !!this.studioBaseThemeId;
    if (switchesToStudio || restoresStudioTheme) {
      body['page_theme'] = this.studioBaseThemeId;
      body['editor'] = 'studio';
    }
    if (switchesToStudio) {
      body['page_option'] = {
        ...(body['page_option'] || {}),
        classic_page_theme: this.classicPageTheme,
      };
    }

    // Call save API based on Page mode
    const saveAPI = this.isCreatePage ? 'createCustomPage' : 'updateCustomPage';

    this.svc[saveAPI](body, this.bearer).subscribe(
      (res: any) => {
        this.showLeaveConfirmation = false;
        this.customPageIsSaving = false;
        this.hasUnsavedChanges = false;
        if (switchesToStudio || restoresStudioTheme) {
          this.isClassicUpgrade = false;
          this.lostStudioTheme = false;
          this.pendingUpgradePayload = null;
          this.selectedTheme = this.studioBaseThemeId;
          this.customPageData.page_theme = this.studioBaseThemeId;
        }
        const successMsg = this.isCreatePage
          ? 'New custom page has been added!'
          : 'Custom page has been updated!';
        this.utils.notify.showSuccess(successMsg);

        if (saveBehaviour === SAVE_BEHAVIOUR.SAVE_AND_EDIT) {
          // Save & Edit
          this.router
            .navigateByUrl('/', { skipLocationChange: true })
            .then(() => {
              this.router.navigateByUrl(
                '/dashboard/page/edit/' + this.storePageId,
              );
            });
        } else if (saveBehaviour === SAVE_BEHAVIOUR.SAVE_AND_CREATE_NEW) {
          this.router.navigate(['/dashboard/pages']);
        } else if (saveBehaviour === SAVE_BEHAVIOUR.SAVE) {
          // Return to Page listing
          this.router.navigate(['/dashboard/pages']);
        }
      },
      (error) => {
        this.showApiError(error);
        this.customPageIsSaving = false;
      },
    );
  }

  filterNonUrlCharacters(event) {
    const raw = (event.target.value || '').toLowerCase();

    if (/(:\/\/|\.com|\.net|\.org|\.io|www\.)/.test(raw)) {
      this.pageUrlSlugWarning = 'Please enter only the page slug, not a full URL.';
    } else if (raw.includes('/')) {
      this.pageUrlSlugWarning = 'Slashes are not allowed. Enter just the slug (e.g. my-product-page).';
    } else if (raw !== raw.replace(this.urlSlugRegex, '')) {
      this.pageUrlSlugWarning = 'Only lowercase letters, numbers, and hyphens are allowed.';
    } else {
      this.pageUrlSlugWarning = '';
    }

    const clean = raw.replace(this.urlSlugRegex, '');
    event.target.value = clean;
    this.customPageData.page_url = clean;
  }

  blockNonUrlCharacters(event): boolean {
    if (event.key && event.key.length > 1) {
      return true;
    }
    return !this.urlSlugRegex.test(event.key);
  }

  // OG image validation rules (Open Graph link preview)
  readonly OG_IMAGE_MIN_WIDTH = 200;
  readonly OG_IMAGE_MIN_HEIGHT = 200;
  readonly OG_IMAGE_MAX_BYTES = 8 * 1024 * 1024; // 8 MB

  ogImgChanged(event) {
    const url = event?.url;

    // Cleared selection — accept and exit.
    if (!url) {
      this.customPageData.metadata.og_image = '';
      return;
    }

    const prevUrl = this.customPageData.metadata.og_image ?? '';
    const prevMediaId = this.ogImgPhotoId;

    // Optimistically apply, then validate. Revert if validation fails.
    this.customPageData.metadata.og_image = url;

    this.validateOgImage(url)
      .then(() => {
        // valid — nothing else to do
      })
      .catch(({ key, values }: { key: string; values: any[] }) => {
        // Revert UI + bound model
        this.ogimgURL = prevUrl;
        this.ogImgPhotoId = prevMediaId;
        this.customPageData.metadata.og_image = prevUrl;

        this.utils.notify.showError(
          this.utils.translate(key, values),
          this.utils.translate('Invalid Open Graph image'),
        );
      });
  }

  private validateOgImage(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // 1. Dimension check
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = async () => {
        const w = img.naturalWidth;
        const h = img.naturalHeight;

        if (w < this.OG_IMAGE_MIN_WIDTH || h < this.OG_IMAGE_MIN_HEIGHT) {
          return reject({
            key: 'Image is too small ({0}×{1}). Minimum dimensions are 200×200 pixels.',
            values: [w, h],
          });
        }

        // 2. File size check (best-effort: HEAD then GET fallback).
        try {
          const bytes = await this.getRemoteImageSize(url);
          if (bytes !== null && bytes > this.OG_IMAGE_MAX_BYTES) {
            const mb = (bytes / (1024 * 1024)).toFixed(2);
            return reject({
              key: 'Image file is too large ({0} MB). Maximum file size is 8 MB.',
              values: [mb],
            });
          }
          resolve();
        } catch {
          // Size couldn't be determined — don't block the user on a network/CORS quirk.
          resolve();
        }
      };
      img.onerror = () =>
        reject({
          key: 'Could not load the selected image. Please try a different file.',
          values: [],
        });
      img.src = url;
    });
  }

  private async getRemoteImageSize(url: string): Promise<number | null> {
    try {
      const head = await fetch(url, { method: 'HEAD' });
      const len = head.headers.get('content-length');
      if (len) return parseInt(len, 10);
    } catch {
      // fall through to GET
    }
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      return blob.size;
    } catch {
      return null;
    }
  }

  getConfigOG() {
    this.svc.getStoreConfig(this.bearer, this.storeId, 'og_img').subscribe(
      (res) => {
        this.ogimgURL = res['data']['value'] ?? '';
        this.customPageData.metadata.og_image = res['data']['value'] ?? '';
      },
      (error) => {},
    );
  }

  /**
   * Build the filter options from the categories the rows actually carry, so the
   * dropdown never needs a hardcoded list. `category` is set by the backend
   * (PageTheme::getAllPageTheme) as a comma-separated list of category names,
   * derived from theme_categories_id; rows without it are simply skipped and so
   * only ever appear under "All".
   */
  private buildCategoryList(rows: any[]) {
    const returnArrayData = [{ name: 'All', id: 0, status: 1 }];
    (rows ?? []).forEach((item) => {
      if (item.category === undefined) return;

      const categories = item.category.split(',');
      categories.forEach((catName) => {
        if (!returnArrayData.some((c) => c.name === catName)) {
          returnArrayData.push({
            name: catName,
            id: +item.theme_categories_id,
            status: 1,
          });
        }
      });
    });
    return returnArrayData;
  }

  /** Filter options for the Studio picker, from the status=2 template rows. */
  get studioDropdownCategoryList() {
    return this.buildCategoryList(this.studioTemplateList);
  }

  /**
   * Apply a category filter to the Studio grid. The hardcoded Product and Blank
   * cards are not part of this list and stay visible under every filter.
   */
  onSelectStudioPage(filter: string) {
    this.studioPageFilter = filter;
    this.displayedStudioTemplateList = this.filterThemeCategories(
      this.studioTemplateList,
      [filter],
    );
  }

  filterThemeCategories(themeList: any, filterString: any) {
    if (!themeList || themeList.length === 0) {
      return themeList;
    }

    return themeList.filter((theme: any) => {
      // Filter by category
      return (
        filterString[0] === 'All' ||
        (theme.category !== undefined &&
          this.compareStrings(filterString[0], theme.category))
      );
    });
  }

  compareStrings(substring: string, commaSeparatedString: string): boolean {
    // Split the comma-separated string into an array and trim extra spaces
    const stringArray = commaSeparatedString.split(',').map((s) => s.trim());

    // Check if the substring exists in the array
    return stringArray.includes(substring);
  }
}
